import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Paper,
  Checkbox,
  Tooltip,
  LinearProgress,
  Skeleton,
  Collapse,
  AlertTitle,
  Stack,
  InputAdornment,
} from '@mui/material';
import { StepIndicator, Step as StepType } from './StepIndicator';
import { Info, ListChecks, Zap, CheckCircle } from 'lucide-react';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Engineering as EngineeringIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  NetworkCheck as NetworkIcon,
  Speed as SpeedIcon,
  Code as CodeIcon,
  DataObject as DataObjectIcon,
  Rule as RuleIcon,
  Description as TemplateIcon,
  Category as CategoryIcon,

  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { TagRule, TagTemplate, ServiceConfig } from '../types/tagops';
import { tagopsService } from '../services/tagopsService';
import { RuleWizardProfessional } from './RuleWizardProfessional';
import { useNotifications, createSuccessNotification, createErrorNotification } from './common/NotificationSystem';
import { PermissionButton } from './common/PermissionButton';
import { PermissionIconButton } from './common/PermissionIconButton';
import { PermissionWrapper } from './common/PermissionWrapper';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// Blacklisted tag keys that are reserved for AWS internal use
const BLACKLISTED_TAG_KEYS = [
  'aws:',
  'CreatedBy',
  'EC2ImageBuilderArn',
  'awsApplication',
  'ImageLifecycle',
  'aws-cloudformation*',
  'elasticbeanstalk:',
  'kubernetes.io/',
  'k8s.io/',
  'aws-cdk:',
  'aws-amplify:',
  'aws-copilot:'
];

// AWS Tagging Restrictions based on https://docs.aws.amazon.com/emr/latest/EMR-Serverless-UserGuide/tagging-restrictions.html
const AWS_TAGGING_RESTRICTIONS = {
  MAX_TAGS_PER_RESOURCE: 50,
  MAX_KEY_LENGTH: 128,
  MAX_VALUE_LENGTH: 256,
  ALLOWED_CHARACTERS: /^[a-zA-Z0-9\s._:/=+\-@]*$/,
  RESERVED_PREFIXES: ['aws:', 'AWS:'],
  MAX_TAGS_PER_RESOURCE_MESSAGE: 'Maximum 50 user-created tags per resource',
  MAX_KEY_LENGTH_MESSAGE: 'Tag key cannot exceed 128 Unicode characters',
  MAX_VALUE_LENGTH_MESSAGE: 'Tag value cannot exceed 256 Unicode characters',
  INVALID_CHARACTERS_MESSAGE: 'Tag contains invalid characters. Only letters, numbers, spaces, and . : / = + - @ are allowed',
  RESERVED_PREFIX_MESSAGE: 'Tag key cannot start with "aws:" (case-insensitive) as it is reserved for AWS use',
  EMPTY_KEY_MESSAGE: 'Tag key cannot be an empty string',
  NULL_VALUE_MESSAGE: 'Tag value cannot be null'
};

// Function to check if a tag key is blacklisted
const isTagKeyBlacklisted = (tagKey: string): boolean => {
  return BLACKLISTED_TAG_KEYS.some(blacklistedKey => {
    if (blacklistedKey.endsWith('*')) {
      // Handle wildcard patterns (e.g., "aws-cloudformation*")
      const pattern = blacklistedKey.slice(0, -1);
      return tagKey.startsWith(pattern);
    } else if (blacklistedKey.endsWith(':')) {
      // Handle prefix patterns (e.g., "aws:", "elasticbeanstalk:")
      return tagKey.startsWith(blacklistedKey);
    } else {
      // Handle exact matches
      return tagKey === blacklistedKey;
    }
  });
};

// Comprehensive AWS tagging validation function
const validateAWSTag = (tagKey: string, tagValue: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check for empty key
  if (tagKey.trim() === '') {
    errors.push(AWS_TAGGING_RESTRICTIONS.EMPTY_KEY_MESSAGE);
  }

  // Check for null value (empty string is allowed, but null is not)
  if (tagValue === null || tagValue === undefined) {
    errors.push(AWS_TAGGING_RESTRICTIONS.NULL_VALUE_MESSAGE);
  }

  // Check key length
  if (tagKey.length > AWS_TAGGING_RESTRICTIONS.MAX_KEY_LENGTH) {
    errors.push(AWS_TAGGING_RESTRICTIONS.MAX_KEY_LENGTH_MESSAGE);
  }

  // Check value length
  if (tagValue && tagValue.length > AWS_TAGGING_RESTRICTIONS.MAX_VALUE_LENGTH) {
    errors.push(AWS_TAGGING_RESTRICTIONS.MAX_VALUE_LENGTH_MESSAGE);
  }

  // Check for reserved AWS prefixes (case-insensitive)
  const lowerTagKey = tagKey.toLowerCase();
  if (AWS_TAGGING_RESTRICTIONS.RESERVED_PREFIXES.some(prefix => 
    lowerTagKey.startsWith(prefix.toLowerCase())
  )) {
    errors.push(AWS_TAGGING_RESTRICTIONS.RESERVED_PREFIX_MESSAGE);
  }

  // Check for blacklisted keys
  if (isTagKeyBlacklisted(tagKey)) {
    errors.push(`Tag key "${tagKey}" is reserved for AWS internal use`);
  }

  // Check for invalid characters in key
  if (tagKey && !AWS_TAGGING_RESTRICTIONS.ALLOWED_CHARACTERS.test(tagKey)) {
    errors.push(AWS_TAGGING_RESTRICTIONS.INVALID_CHARACTERS_MESSAGE);
  }

  // Check for invalid characters in value
  if (tagValue && !AWS_TAGGING_RESTRICTIONS.ALLOWED_CHARACTERS.test(tagValue)) {
    errors.push(AWS_TAGGING_RESTRICTIONS.INVALID_CHARACTERS_MESSAGE);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Enhanced RuleEditor with professional features for technical teams
interface RuleEditorEnhancedProps {
  onSave: (rules: any) => void;
}

// Professional color scheme for technical teams

// Professional rule categories for different teams
const RULE_CATEGORIES = {
  'cost-management': {
    name: 'Cost Management',
    icon: <BusinessIcon />,
    color: '#2e7d32',
    description: 'Rules for cost allocation and optimization',
    tags: ['cost-center', 'budget', 'billing', 'finops'],
    examples: [
      'Automatically tag resources with cost center',
      'Apply budget allocation tags',
      'Tag resources for billing purposes',
    ],
  },
  'security-compliance': {
    name: 'Security & Compliance',
    icon: <SecurityIcon />,
    color: '#d32f2f',
    description: 'Rules for security and compliance requirements',
    tags: ['security', 'compliance', 'audit', 'governance'],
    examples: [
      'Tag resources with security classification',
      'Apply compliance framework tags',
      'Mark resources for audit purposes',
    ],
  },
  'operations': {
    name: 'Operations',
    icon: <EngineeringIcon />,
    color: '#1976d2',
    description: 'Rules for operational management',
    tags: ['operations', 'maintenance', 'monitoring', 'devops'],
    examples: [
      'Tag resources with team ownership',
      'Apply maintenance window tags',
      'Mark resources for monitoring',
    ],
  },
  'development': {
    name: 'Development',
    icon: <CodeIcon />,
    color: '#ed6c02',
    description: 'Rules for development and deployment',
    tags: ['development', 'deployment', 'environment', 'ci-cd'],
    examples: [
      'Tag resources by environment',
      'Apply deployment stage tags',
      'Mark resources for CI/CD',
    ],
  },
  'data-governance': {
    name: 'Data Governance',
    icon: <DataObjectIcon />,
    color: '#7b1fa2',
    description: 'Rules for data classification and governance',
    tags: ['data', 'classification', 'privacy', 'retention'],
    examples: [
      'Tag resources with data classification',
      'Apply privacy level tags',
      'Mark resources for retention policies',
    ],
  },
  'network': {
    name: 'Network',
    icon: <NetworkIcon />,
    color: '#1565c0',
    description: 'Rules for network resources',
    tags: ['network', 'vpc', 'subnet', 'security-group'],
    examples: [
      'Tag network resources by purpose',
      'Apply VPC and subnet tags',
      'Mark security groups by function',
    ],
  },
  'storage': {
    name: 'Storage',
    icon: <StorageIcon />,
    color: '#388e3c',
    description: 'Rules for storage resources',
    tags: ['storage', 's3', 'ebs', 'backup'],
    examples: [
      'Tag storage by type and purpose',
      'Apply backup policy tags',
      'Mark storage for lifecycle management',
    ],
  },
  'compute': {
    name: 'Compute',
    icon: <SpeedIcon />,
    color: '#f57c00',
    description: 'Rules for compute resources',
    tags: ['compute', 'ec2', 'lambda', 'container'],
    examples: [
      'Tag compute resources by workload',
      'Apply instance type tags',
      'Mark containers by service',
    ],
  },
};

// Supported dynamic tag types for templates
const DYNAMIC_TAG_TYPES = [
  'created-by',
  'creation-date', 
  'region',
  'accountid'
];

// Professional rule templates for quick setup
const PROFESSIONAL_TEMPLATES = {
  // COST MANAGEMENT TEMPLATES
  'cost-center': {
    name: 'Cost Center Template',
    description: 'Standard cost allocation tags',
    category: 'cost-management',
    tags: [
      { key: 'CostCenter', value: '' },
      { key: 'Project', value: '' },
      { key: 'Department', value: '' },
      { key: 'BudgetOwner', value: '' },
    ],
  },
  'business-unit': {
    name: 'Business Unit Template',
    description: 'Business unit cost allocation for financial reporting',
    category: 'cost-management',
    tags: [
      { key: 'BusinessUnit', value: '' },
      { key: 'CostCenter', value: '' },
      { key: 'ProfitCenter', value: '' },
      { key: 'FinanceOwner', value: '' },
      { key: 'BudgetCode', value: '' },
    ],
  },
  'customer-billing': {
    name: 'Customer Billing Template',
    description: 'Customer-specific cost allocation for billing',
    category: 'cost-management',
    tags: [
      { key: 'Customer', value: '' },
      { key: 'CustomerType', value: '' },
      { key: 'BillingCode', value: '' },
      { key: 'RevenueStream', value: '' },
      { key: 'ContractId', value: '' },
    ],
  },
  'project-cost-tracking': {
    name: 'Project Cost Tracking Template',
    description: 'Project-based cost tracking and allocation',
    category: 'cost-management',
    tags: [
      { key: 'Project', value: '' },
      { key: 'ProjectPhase', value: '' },
      { key: 'ProjectManager', value: '' },
      { key: 'ProjectBudget', value: '' },
      { key: 'ProjectCode', value: '' },
    ],
  },
  'resource-optimization': {
    name: 'Resource Optimization Template',
    description: 'Tags for cost optimization and resource management',
    category: 'cost-management',
    tags: [
      { key: 'CostOptimization', value: 'enabled' },
      { key: 'AutoShutdown', value: 'enabled' },
      { key: 'ResourceTier', value: 'standard' },
      { key: 'OptimizationPriority', value: 'medium' },
    ],
  },

  // SECURITY & ABAC TEMPLATES
  'security-classification': {
    name: 'Security Classification Template',
    description: 'Data security classification tags',
    category: 'security-compliance',
    tags: [
      { key: 'DataClassification', value: 'Internal' },
      { key: 'SecurityLevel', value: 'Standard' },
      { key: 'ComplianceFramework', value: '' },
      { key: 'DataRetention', value: '7years' },
    ],
  },
  'abac-access-control': {
    name: 'ABAC Access Control Template',
    description: 'Attribute-based access control tags for IAM policies',
    category: 'security-compliance',
    tags: [
      { key: 'AccessLevel', value: 'standard' },
      { key: 'DataSensitivity', value: 'internal' },
      { key: 'ComplianceRequirement', value: '' },
      { key: 'AccessRole', value: '' },
      { key: 'SecurityZone', value: 'standard' },
    ],
  },
  'data-privacy': {
    name: 'Data Privacy Template',
    description: 'Data privacy and protection classification',
    category: 'security-compliance',
    tags: [
      { key: 'DataPrivacy', value: 'standard' },
      { key: 'PIILevel', value: 'none' },
      { key: 'DataRetention', value: '7years' },
      { key: 'EncryptionRequired', value: 'yes' },
      { key: 'AuditLevel', value: 'standard' },
    ],
  },
  'compliance-framework': {
    name: 'Compliance Framework Template',
    description: 'Compliance framework and regulatory requirements',
    category: 'security-compliance',
    tags: [
      { key: 'ComplianceFramework', value: '' },
      { key: 'RegulatoryRequirement', value: '' },
      { key: 'AuditScope', value: 'included' },
      { key: 'ComplianceOwner', value: '' },
      { key: 'ReviewCycle', value: 'quarterly' },
    ],
  },
  'security-baseline': {
    name: 'Security Baseline Template',
    description: 'Security baseline and hardening requirements',
    category: 'security-compliance',
    tags: [
      { key: 'SecurityBaseline', value: 'standard' },
      { key: 'HardeningLevel', value: 'standard' },
      { key: 'SecurityScanning', value: 'enabled' },
      { key: 'VulnerabilityManagement', value: 'enabled' },
    ],
  },
  'aws-inspector-cis': {
    name: 'AWS Inspector CIS Template',
    description: 'Template for performing CIS scans on EC2 instances using AWS Inspector',
    category: 'security-compliance',
    tags: [
      { key: 'cis-scan', value: 'true' },
    ],
  },
  'aws-patch-manager': {
    name: 'AWS Patch Manager Template',
    description: 'Template for organizing EC2 instances into patch groups for automated patching operations',
    category: 'operations',
    tags: [
      { key: 'PatchGroup', value: '' },
    ],
  },

  // AUTOMATION & LIFECYCLE TEMPLATES
  'automation-lifecycle': {
    name: 'Automation Lifecycle Template',
    description: 'Tags for automated resource lifecycle management',
    category: 'operations',
    tags: [
      { key: 'AutoShutdown', value: 'enabled' },
      { key: 'LifecycleStage', value: 'active' },
      { key: 'AutomationLevel', value: 'full' },
      { key: 'MaintenanceWindow', value: 'weekend' },
    ],
  },
  'backup-disaster-recovery': {
    name: 'Backup & DR Template',
    description: 'Backup and disaster recovery configuration',
    category: 'operations',
    tags: [
      { key: 'BackupSchedule', value: 'daily' },
      { key: 'BackupRetention', value: '30days' },
      { key: 'DisasterRecovery', value: 'enabled' },
      { key: 'RTO', value: '4hours' },
      { key: 'RPO', value: '1hour' },
    ],
  },
  'monitoring-alerting': {
    name: 'Monitoring & Alerting Template',
    description: 'Monitoring and alerting configuration',
    category: 'operations',
    tags: [
      { key: 'MonitoringLevel', value: 'standard' },
      { key: 'AlertingEnabled', value: 'yes' },
      { key: 'OnCallTeam', value: '' },
      { key: 'EscalationLevel', value: 'standard' },
    ],
  },

  // DEVELOPMENT & DEPLOYMENT TEMPLATES
  'environment': {
    name: 'Environment Template',
    description: 'Environment and deployment tags',
    category: 'development',
    tags: [
      { key: 'Environment', value: '' },
      { key: 'DeploymentStage', value: '' },
      { key: 'Version', value: '' },
      { key: 'ManagedBy', value: 'TagOps' },
    ],
  },
  'ci-cd-pipeline': {
    name: 'CI/CD Pipeline Template',
    description: 'Continuous integration and deployment tags',
    category: 'development',
    tags: [
      { key: 'PipelineStage', value: '' },
      { key: 'BuildNumber', value: '' },
      { key: 'GitBranch', value: '' },
      { key: 'DeploymentMethod', value: 'automated' },
      { key: 'RollbackEnabled', value: 'yes' },
    ],
  },
  'application-architecture': {
    name: 'Application Architecture Template',
    description: 'Application and architecture classification',
    category: 'development',
    tags: [
      { key: 'Application', value: '' },
      { key: 'ApplicationTier', value: '' },
      { key: 'ArchitecturePattern', value: '' },
      { key: 'TechnologyStack', value: '' },
      { key: 'Dependencies', value: '' },
    ],
  },

  // DATA GOVERNANCE TEMPLATES
  'data-classification': {
    name: 'Data Classification Template',
    description: 'Data classification and governance',
    category: 'data-governance',
    tags: [
      { key: 'DataClassification', value: 'internal' },
      { key: 'DataType', value: '' },
      { key: 'DataOwner', value: '' },
      { key: 'DataRetention', value: '7years' },
      { key: 'DataLineage', value: 'tracked' },
    ],
  },
  'data-privacy-gdpr': {
    name: 'Data Privacy GDPR Template',
    description: 'GDPR and data privacy compliance',
    category: 'data-governance',
    tags: [
      { key: 'GDPRCompliant', value: 'yes' },
      { key: 'DataSubjectRights', value: 'enabled' },
      { key: 'DataProcessingBasis', value: '' },
      { key: 'DataController', value: '' },
      { key: 'DataProcessor', value: '' },
    ],
  },

  // OPERATIONS TEMPLATES
  'operations': {
    name: 'Operations Template',
    description: 'Operational management tags',
    category: 'operations',
    tags: [
      { key: 'Team', value: '' },
      { key: 'OnCall', value: '' },
      { key: 'MaintenanceWindow', value: '' },
      { key: 'BackupSchedule', value: 'Daily' },
    ],
  },
  'incident-management': {
    name: 'Incident Management Template',
    description: 'Incident management and response tags',
    category: 'operations',
    tags: [
      { key: 'IncidentPriority', value: 'medium' },
      { key: 'ResponseTeam', value: '' },
      { key: 'EscalationPath', value: 'standard' },
      { key: 'CommunicationPlan', value: 'enabled' },
    ],
  },
  'capacity-planning': {
    name: 'Capacity Planning Template',
    description: 'Capacity planning and resource sizing',
    category: 'operations',
    tags: [
      { key: 'CapacityTier', value: 'standard' },
      { key: 'ScalingPolicy', value: 'auto' },
      { key: 'ResourceQuota', value: '' },
      { key: 'PerformanceTier', value: 'standard' },
    ],
  },
};

// Helper functions to extract rule information for display
const getRuleActionType = (rule: TagRule): string => {
  if (rule.rule?.type === 'add') {
    return 'Add Tags';
  } else if (rule.rule?.type === 'remove') {
    return 'Remove Tags';
  } else if (rule.rule?.type === 'template') {
    return 'Apply Template';
  }
  return 'Unknown Action';
};

// Get rule names that contain a given tag key-value pair (exact match)
const getRulesContainingTag = (
  rules: Record<string, TagRule>,
  templates: Record<string, TagTemplate>,
  tagKey: string,
  tagValue: string,
  excludeRuleName?: string | null
): string[] => {
  if (!tagKey?.trim()) return [];
  const result: string[] = [];
  const normalizedKey = tagKey.trim();
  const normalizedValue = (tagValue ?? '').trim();

  Object.entries(rules).forEach(([ruleName, rule]) => {
    if (excludeRuleName && ruleName === excludeRuleName) return;

    if (rule.rule?.type === 'add' || rule.rule?.type === 'remove') {
      const ruleTags = (rule.rule.tags || []) as Array<Record<string, string>>;
      const hasMatch = ruleTags.some(tagObj => {
        const k = Object.keys(tagObj)[0]?.trim() ?? '';
        const v = (Object.values(tagObj)[0] ?? '').trim();
        return k === normalizedKey && v === normalizedValue;
      });
      if (hasMatch) result.push(ruleName);
    } else if (rule.rule?.type === 'template' && rule.rule.templateName) {
      const template = templates[rule.rule.templateName];
      const constantTags = template?.constantTags ?? [];
      const hasMatch = constantTags.some(tagObj => {
        const k = Object.keys(tagObj)[0]?.trim() ?? '';
        const v = (Object.values(tagObj)[0] ?? '').trim();
        return k === normalizedKey && v === normalizedValue;
      });
      if (hasMatch) result.push(ruleName);
    }
  });
  return result;
};

// Get rules with similar tag key (same key case-insensitive, different casing) - regardless of value
const getRulesContainingSimilarTag = (
  rules: Record<string, TagRule>,
  templates: Record<string, TagTemplate>,
  tagKey: string,
  _tagValue: string,
  excludeRuleName?: string | null
): Array<{ ruleName: string; existingKey: string }> => {
  if (!tagKey?.trim()) return [];
  const result: Array<{ ruleName: string; existingKey: string }> = [];
  const normalizedKey = tagKey.trim();
  const normalizedKeyLower = normalizedKey.toLowerCase();

  const collectSimilar = (ruleName: string, k: string) => {
    // Same key case-insensitive, but different casing (e.g. CostCenter vs Costcenter)
    if (k.toLowerCase() === normalizedKeyLower && k !== normalizedKey) {
      result.push({ ruleName, existingKey: k });
    }
  };

  Object.entries(rules).forEach(([ruleName, rule]) => {
    if (excludeRuleName && ruleName === excludeRuleName) return;

    if (rule.rule?.type === 'add' || rule.rule?.type === 'remove') {
      const ruleTags = (rule.rule.tags || []) as Array<Record<string, string>>;
      ruleTags.forEach(tagObj => {
        const k = Object.keys(tagObj)[0]?.trim() ?? '';
        collectSimilar(ruleName, k);
      });
    } else if (rule.rule?.type === 'template' && rule.rule.templateName) {
      const template = templates[rule.rule.templateName];
      const constantTags = template?.constantTags ?? [];
      constantTags.forEach(tagObj => {
        const k = Object.keys(tagObj)[0]?.trim() ?? '';
        collectSimilar(ruleName, k);
      });
    }
  });
  return result;
};

const getRuleTags = (rule: TagRule): Array<{ key: string; value: string }> => {
  const tags: Array<{ key: string; value: string }> = [];
  
  if (rule.rule?.type === 'add' && rule.rule.tags) {
    const ruleTags = rule.rule.tags as Array<Record<string, string>>;
    ruleTags.forEach(tagObj => {
      Object.entries(tagObj).forEach(([key, value]) => {
        tags.push({ key, value: value || '' });
      });
    });
  } else if (rule.rule?.type === 'remove' && rule.rule.tags) {
    const ruleTags = rule.rule.tags as Array<Record<string, string>>;
    ruleTags.forEach(tagObj => {
      Object.entries(tagObj).forEach(([key, value]) => {
        tags.push({ key, value: value || '' });
      });
    });
  } else if (rule.rule?.type === 'template' && rule.rule.templateName) {
    tags.push({ key: 'Template', value: rule.rule.templateName });
  }
  
  return tags;
};

const getRuleConditions = (rule: TagRule): string[] => {
  const conditions: string[] = [];
  
  if (rule.condition && rule.condition.length > 0) {
    rule.condition.forEach(condition => {
      let conditionText = '';
      
      switch (condition.type) {
        case 'resourceType':
          conditionText = `Resource Type ${condition.operator} ${Array.isArray(condition.value) ? condition.value.join(', ') : condition.value}`;
          break;
        case 'region':
          conditionText = `Region ${condition.operator} ${Array.isArray(condition.value) ? condition.value.join(', ') : condition.value}`;
          break;
        case 'accountId':
          conditionText = `Account ${condition.operator} ${Array.isArray(condition.value) ? condition.value.join(', ') : condition.value}`;
          break;
        case 'serviceResourceType':
          conditionText = `Service ${condition.operator} ${Array.isArray(condition.value) ? condition.value.join(', ') : condition.value}`;
          break;
        case 'name':
          conditionText = `Resource Name ${condition.operator} ${Array.isArray(condition.value) ? condition.value.join(', ') : condition.value}`;
          break;
        case 'tagKey':
          conditionText = `Tag Key ${condition.operator} ${Array.isArray(condition.value) ? condition.value.join(', ') : condition.value}`;
          break;
        case 'tagKeyValue':
          if (condition.tag_key_operator && condition.tag_key_value && condition.tag_value_operator && condition.tag_value_value) {
            const tagKeyValueStr = Array.isArray(condition.tag_key_value) 
              ? condition.tag_key_value.join(', ') 
              : condition.tag_key_value;
            const tagValueValueStr = Array.isArray(condition.tag_value_value) 
              ? condition.tag_value_value.join(', ') 
              : condition.tag_value_value;
            conditionText = `Tag Key ${condition.tag_key_operator} ${tagKeyValueStr} AND Tag Value ${condition.tag_value_operator} ${tagValueValueStr}`;
          } else {
            conditionText = `Tag Key Value (incomplete)`;
          }
          break;
        case 'createdTime': {
          const op = condition.operator || 'createdBefore';
          const raw = condition.value;
          let epoch = NaN;
          if (typeof raw === 'number' && !Number.isNaN(raw)) epoch = raw;
          else if (typeof raw === 'string' && /^\d+$/.test(raw)) epoch = parseInt(raw, 10);
          let timePart = String(condition.value ?? '');
          if (!Number.isNaN(epoch) && epoch > 0) {
            try {
              timePart = new Date(epoch * 1000).toLocaleString();
            } catch {
              timePart = String(raw);
            }
          }
          const unk =
            Array.isArray(condition.flags) && condition.flags.includes('allow_unknown_creation_time')
              ? ' (allow unknown creation time)'
              : '';
          conditionText = `Created Time ${op} ${timePart}${unk}`;
          break;
        }
        default:
          conditionText = `${condition.type} ${condition.operator} ${Array.isArray(condition.value) ? condition.value.join(', ') : condition.value}`;
      }
      
      conditions.push(conditionText);
    });
  }
  
  return conditions;
};

const getRuleCategoryInfo = (rule: TagRule) => {
  // Try to get category from rule data, fallback to inferring from tags
  if (rule.category) {
    return RULE_CATEGORIES[rule.category as keyof typeof RULE_CATEGORIES] || null;
  }
  
  // Infer category from tags
  const tags = rule.rule?.tags as Array<Record<string, string>> || [];
  const tagKeys = tags.map(tag => Object.keys(tag)[0]).join(' ').toLowerCase();
  
  if (tagKeys.includes('cost') || tagKeys.includes('budget') || tagKeys.includes('project')) {
    return RULE_CATEGORIES['cost-management'];
  } else if (tagKeys.includes('security') || tagKeys.includes('compliance') || tagKeys.includes('classification')) {
    return RULE_CATEGORIES['security-compliance'];
  } else if (tagKeys.includes('environment') || tagKeys.includes('deployment') || tagKeys.includes('version')) {
    return RULE_CATEGORIES['development'];
  } else if (tagKeys.includes('team') || tagKeys.includes('oncall') || tagKeys.includes('maintenance')) {
    return RULE_CATEGORIES['operations'];
  }
  
  return null;
};

// Helper function to generate duplicate rule names
const generateDuplicateRuleName = (originalName: string, existingRules: Record<string, TagRule>): string => {
  let counter = 1;
  let newName = `${originalName}#duplicated`;
  
  while (existingRules[newName]) {
    counter++;
    newName = `${originalName}#duplicated${counter}`;
  }
  
  return newName;
};

// View mode helper functions for adaptive UI
// Helper function to get next priority
const getNextPriority = (rules: Record<string, TagRule>): number => {
  const priorities = Object.values(rules)
    .map(rule => rule.priority ?? 9999)
    .filter(p => p !== 9999 && p < 9999);
  return priorities.length > 0 ? Math.max(...priorities) + 1 : 1;
};

const renderCompactRuleCard = (
  name: string,
  rule: TagRule,
  selectedRules: string[],
  setSelectedRules: (rules: string[]) => void,
  handleEditRule: (name: string, rule: TagRule) => void,
  handleDuplicateRule: (name: string, rule: TagRule) => void,
  handleDeleteRule: (name: string) => void,
  isDragging?: boolean,
  dragHandleProps?: any
) => {
  const actionType = getRuleActionType(rule);
  const categoryInfo = getRuleCategoryInfo(rule);
  const priority = rule.priority ?? 9999;
  
  return (
    <Paper
      key={name}
      sx={{
        p: 1.5,
        mb: 1.5,
        border: '1px solid',
        borderColor: selectedRules.includes(name) ? 'primary.main' : 'divider',
        bgcolor: selectedRules.includes(name) ? 'primary.50' : 'background.paper',
        cursor: isDragging ? 'grabbing' : 'pointer',
        opacity: isDragging ? 0.5 : 1,
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 1,
        },
      }}
      onClick={() => {
        if (selectedRules.includes(name)) {
          setSelectedRules(selectedRules.filter(r => r !== name));
        } else {
          setSelectedRules([...selectedRules, name]);
        }
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5}>
        {/* Drag Handle */}
        {dragHandleProps && (
          <Box
            {...dragHandleProps}
            sx={{
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              '&:active': {
                cursor: 'grabbing',
              },
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
        
        <Checkbox
          checked={selectedRules.includes(name)}
          onChange={(e) => e.stopPropagation()}
          size="small"
        />
        
        {/* Priority Badge */}
        <Chip
          label={`#${priority}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 24, minWidth: 50, fontWeight: 'bold' }}
        />
        
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="subtitle1" fontWeight="600" noWrap>
              {name}
            </Typography>
            <Chip
              label={actionType}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
            {rule.enabled === false ? (
              <Chip
                label="Disabled"
                size="small"
                color="error"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            ) : (
              <Chip
                label="Enabled"
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Box>
          
          {categoryInfo && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box color={categoryInfo.color} sx={{ display: 'flex', alignItems: 'center' }}>
                {categoryInfo.icon}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {categoryInfo.name}
              </Typography>
            </Box>
          )}
        </Box>
        
        <Box display="flex" gap={0.5}>
          <Tooltip title="Edit rule">
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditRule(name, rule);
              }}
            >
              <EditIcon fontSize="small" />
            </PermissionIconButton>
          </Tooltip>
          <Tooltip title="Duplicate rule">
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateRule(name, rule);
              }}
            >
              <CopyIcon fontSize="small" />
            </PermissionIconButton>
          </Tooltip>
          <Tooltip title="Delete rule">
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteRule(name);
              }}
            >
              <DeleteIcon fontSize="small" />
            </PermissionIconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

// DndContext Wrapper Component
const DndContextWrapper = ({
  filteredRules,
  rules,
  setRules,
  setSaving,
  handleSaveRules,
  showNotification,
  createSuccessNotification,
  createErrorNotification,
  viewMode,
  selectedRules,
  setSelectedRules,
  handleEditRule,
  handleDuplicateRule,
  handleDeleteRule,
  handleToggleRuleStatus,
  renderCompactRuleCard,
  renderDetailedRuleCard,
  loadData,
}: {
  filteredRules: Array<[string, TagRule]>;
  rules: Record<string, TagRule>;
  setRules: (rules: Record<string, TagRule>) => void;
  setSaving: (saving: boolean) => void;
  handleSaveRules: (rules: Record<string, TagRule>) => Promise<void>;
  showNotification: (notification: any) => void;
  createSuccessNotification: (title: string, message: string) => any;
  createErrorNotification: (error: any, retryAction?: () => void, customMessage?: string) => Omit<any, 'id'>;
  viewMode: 'compact' | 'detailed';
  selectedRules: string[];
  setSelectedRules: (rules: string[]) => void;
  handleEditRule: (name: string, rule: TagRule) => void;
  handleDuplicateRule: (name: string, rule: TagRule) => void;
  handleDeleteRule: (name: string) => void;
  handleToggleRuleStatus: (name: string, rule: TagRule) => void;
  renderCompactRuleCard: any;
  renderDetailedRuleCard: any;
  loadData: () => Promise<void>;
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = filteredRules.findIndex(([name]) => name === active.id);
      const newIndex = filteredRules.findIndex(([name]) => name === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Reorder the filtered rules array
        const reorderedFilteredRules = arrayMove(filteredRules, oldIndex, newIndex);
        
        // Get all rule names in the new priority order (only filtered rules)
        const reorderedRuleNames = reorderedFilteredRules.map(([name]) => name);
        
        // Get all other rules (not in filtered list) and sort them by current priority
        const allRuleNames = Object.keys(rules);
        const otherRuleNames = allRuleNames.filter(name => !reorderedRuleNames.includes(name));
        const otherRulesWithPriority = otherRuleNames
          .map(name => ({ name, priority: rules[name].priority ?? 9999 }))
          .sort((a, b) => a.priority - b.priority);
        
        // Calculate new priorities:
        // - Reordered rules get priorities 1, 2, 3... based on their new position
        // - Other rules keep their relative order but get shifted priorities
        const updatedRules: Record<string, TagRule> = { ...rules };
        
        // Update priorities for reordered rules
        reorderedRuleNames.forEach((ruleName, index) => {
          if (updatedRules[ruleName]) {
            updatedRules[ruleName] = {
              ...updatedRules[ruleName],
              priority: index + 1,
            };
          }
        });
        
        // Update priorities for other rules (shift them after the reordered ones)
        const startPriority = reorderedRuleNames.length + 1;
        otherRulesWithPriority.forEach(({ name }, index) => {
          if (updatedRules[name]) {
            updatedRules[name] = {
              ...updatedRules[name],
              priority: startPriority + index,
            };
          }
        });
        
        setRules(updatedRules);
        
        // Save to backend
        setSaving(true);
        try {
          await handleSaveRules(updatedRules);
          showNotification(createSuccessNotification('Success', 'Rule order updated successfully'));
        } catch (error) {
          showNotification(createErrorNotification(error, undefined, 'Failed to update rule order'));
          // Revert on error
          setRules(rules);
        } finally {
          setSaving(false);
        }
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={filteredRules.map(([name]) => name)}
        strategy={verticalListSortingStrategy}
      >
        {filteredRules.map(([name, rule]) => (
          <motion.div
            key={name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <SortableRuleCard
              id={name}
              name={name}
              rule={rule}
              selectedRules={selectedRules}
              setSelectedRules={setSelectedRules}
              handleEditRule={handleEditRule}
              handleDuplicateRule={handleDuplicateRule}
              handleDeleteRule={handleDeleteRule}
              viewMode={viewMode}
              renderCompactRuleCard={renderCompactRuleCard}
              renderDetailedRuleCard={renderDetailedRuleCard}
              handleToggleRuleStatus={handleToggleRuleStatus}
            />
          </motion.div>
        ))}
      </SortableContext>
    </DndContext>
  );
};

// Sortable Rule Card Component
const SortableRuleCard = ({
  id,
  name,
  rule,
  selectedRules,
  setSelectedRules,
  handleEditRule,
  handleDuplicateRule,
  handleDeleteRule,
  viewMode,
  renderCompactRuleCard,
  renderDetailedRuleCard,
  handleToggleRuleStatus,
}: {
  id: string;
  name: string;
  rule: TagRule;
  selectedRules: string[];
  setSelectedRules: (rules: string[]) => void;
  handleEditRule: (name: string, rule: TagRule) => void;
  handleDuplicateRule: (name: string, rule: TagRule) => void;
  handleDeleteRule: (name: string) => void;
  viewMode: 'compact' | 'detailed';
  renderCompactRuleCard: any;
  renderDetailedRuleCard: any;
  handleToggleRuleStatus: (name: string, rule: TagRule) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {viewMode === 'compact'
        ? renderCompactRuleCard(
            name,
            rule,
            selectedRules,
            setSelectedRules,
            handleEditRule,
            handleDuplicateRule,
            handleDeleteRule,
            isDragging,
            { ...attributes, ...listeners }
          )
        : renderDetailedRuleCard(
            name,
            rule,
            selectedRules,
            setSelectedRules,
            handleEditRule,
            handleDuplicateRule,
            handleDeleteRule,
            handleToggleRuleStatus,
            isDragging,
            { ...attributes, ...listeners }
          )}
    </div>
  );
};

const renderDetailedRuleCard = (
  name: string,
  rule: TagRule,
  selectedRules: string[],
  setSelectedRules: (rules: string[]) => void,
  handleEditRule: (name: string, rule: TagRule) => void,
  handleDuplicateRule: (name: string, rule: TagRule) => void,
  handleDeleteRule: (name: string) => void,
  handleToggleRuleStatus: (name: string, rule: TagRule) => void,
  isDragging?: boolean,
  dragHandleProps?: any
) => {
  const actionType = getRuleActionType(rule);
  const tags = getRuleTags(rule);
  const conditions = getRuleConditions(rule);
  const categoryInfo = getRuleCategoryInfo(rule);
  const priority = rule.priority ?? 9999;
  
  return (
    <Paper
      key={name}
      sx={{
        p: 2,
        mb: 2,
        border: '1px solid',
        borderColor: selectedRules.includes(name) ? 'primary.main' : 'divider',
        bgcolor: selectedRules.includes(name) ? 'primary.50' : 'background.paper',
        cursor: isDragging ? 'grabbing' : 'pointer',
        opacity: isDragging ? 0.5 : 1,
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 2,
        },
      }}
      onClick={() => {
        if (selectedRules.includes(name)) {
          setSelectedRules(selectedRules.filter(r => r !== name));
        } else {
          setSelectedRules([...selectedRules, name]);
        }
      }}
    >
      <Box display="flex" alignItems="center" gap={2}>
        {/* Drag Handle */}
        {dragHandleProps && (
          <Box
            {...dragHandleProps}
            sx={{
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              '&:active': {
                cursor: 'grabbing',
              },
            }}
          >
            <DragIndicatorIcon />
          </Box>
        )}
        
        <Checkbox
          checked={selectedRules.includes(name)}
          onChange={(e) => e.stopPropagation()}
        />
        
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="h6" fontWeight="bold">
              {name}
            </Typography>
            {/* Priority Badge */}
            <Chip
              label={`Priority: ${priority}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 'bold' }}
            />
            {categoryInfo && (
              <Chip
                icon={categoryInfo.icon}
                label={categoryInfo.name}
                size="small"
                sx={{ 
                  bgcolor: `${categoryInfo.color}15`,
                  color: categoryInfo.color,
                  border: `1px solid ${categoryInfo.color}30`
                }}
              />
            )}
            {rule.enabled === false ? (
              <Chip
                label="Disabled"
                size="small"
                color="error"
                variant="outlined"
              />
            ) : (
              <Chip
                label="Enabled"
                size="small"
                color="success"
                variant="outlined"
              />
            )}
          </Box>
          
          <Typography variant="body2" color="text.secondary" mb={1}>
            {rule.description || 'No description provided'}
          </Typography>
          
          <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
            <Chip
              label={actionType}
              size="small"
              variant="outlined"
              color="primary"
            />
            {conditions.length > 0 && (
              <Chip
                label={`${conditions.length} condition${conditions.length > 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                color="secondary"
              />
            )}
          </Box>
          
          {conditions.length > 0 && (
            <Box mb={1}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Conditions:
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.5}>
                {conditions.slice(0, 3).map((condition, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1,
                      bgcolor: 'primary.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'primary.200',
                      position: 'relative',
                      alignSelf: 'flex-start',
                      width: 'fit-content',
                    }}
                  >
                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 500 }}>
                      {condition}
                    </Typography>
                  </Box>
                ))}
                {conditions.length > 3 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', ml: 1 }}>
                    +{conditions.length - 3} more condition{conditions.length - 3 > 1 ? 's' : ''}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          
          {tags.length > 0 && (
            <Box mb={1}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Tags:
              </Typography>
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {tags.slice(0, 4).map((tag, index) => (
                  <Chip
                    key={index}
                    label={`${tag.key}${tag.value ? `=${tag.value}` : ''}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
                {tags.length > 4 && (
                  <Chip
                    label={`+${tags.length - 4} more`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
        
        <Box display="flex" gap={1}>
          <Tooltip title="Edit rule">
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditRule(name, rule);
              }}
            >
              <EditIcon />
            </PermissionIconButton>
          </Tooltip>
          <Tooltip title="Duplicate rule">
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateRule(name, rule);
              }}
            >
              <CopyIcon />
            </PermissionIconButton>
          </Tooltip>
          <Tooltip title={rule.enabled ? "Disable rule" : "Enable rule"}>
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              color={rule.enabled ? "warning" : "success"}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleRuleStatus(name, rule);
              }}
            >
              {rule.enabled ? <PauseIcon /> : <PlayArrowIcon />}
            </PermissionIconButton>
          </Tooltip>
          <Tooltip title="Delete rule">
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteRule(name);
              }}
            >
              <DeleteIcon />
            </PermissionIconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

// Template view mode helper functions for adaptive UI
const renderCompactTemplateCard = (
  name: string,
  template: TagTemplate,
  handleEditTemplate: (name: string, template: TagTemplate) => void,
  handleDuplicateTemplate: (name: string, template: TagTemplate) => void,
  handleDeleteTemplate: (name: string) => void
) => {
  const categoryInfo = template.category ? RULE_CATEGORIES[template.category as keyof typeof RULE_CATEGORIES] : null;
  
  return (
    <Paper
      key={name}
      sx={{
        p: 1.5,
        mb: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 1,
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5}>
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="subtitle1" fontWeight="600" noWrap>
              {name}
            </Typography>
            {template.generalTags && template.generalTags.length > 0 && (
              <Chip
                label={`${template.generalTags.length} Dynamic`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
            {template.constantTags && template.constantTags.length > 0 && (
              <Chip
                label={`${template.constantTags.length} Constant`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Box>
          
          {categoryInfo && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box color={categoryInfo.color} sx={{ display: 'flex', alignItems: 'center' }}>
                {categoryInfo.icon}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {categoryInfo.name}
              </Typography>
            </Box>
          )}
        </Box>
        
        <Box display="flex" gap={0.5}>
          <Tooltip title="Edit template">
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditTemplate(name, template);
              }}
            >
              <EditIcon fontSize="small" />
            </PermissionIconButton>
          </Tooltip>
          <Tooltip title="Duplicate template">
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateTemplate(name, template);
              }}
            >
              <CopyIcon fontSize="small" />
            </PermissionIconButton>
          </Tooltip>
          <Tooltip title="Delete template">
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteTemplate(name);
              }}
            >
              <DeleteIcon fontSize="small" />
            </PermissionIconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

const renderDetailedTemplateCard = (
  name: string,
  template: TagTemplate,
  handleEditTemplate: (name: string, template: TagTemplate) => void,
  handleDuplicateTemplate: (name: string, template: TagTemplate) => void,
  handleDeleteTemplate: (name: string) => void
) => {
  const categoryInfo = template.category ? RULE_CATEGORIES[template.category as keyof typeof RULE_CATEGORIES] : null;
  
  return (
    <Paper
      key={name}
      sx={{
        p: 2,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 2,
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="h6" fontWeight="bold">
              {name}
            </Typography>
            {categoryInfo && (
              <Chip
                icon={categoryInfo.icon}
                label={categoryInfo.name}
                size="small"
                sx={{ 
                  bgcolor: `${categoryInfo.color}15`,
                  color: categoryInfo.color,
                  border: `1px solid ${categoryInfo.color}30`
                }}
              />
            )}
          </Box>
          
          {template.description && (
            <Typography variant="body2" color="text.secondary" mb={1}>
              {template.description}
            </Typography>
          )}
          
          <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
            {template.generalTags && template.generalTags.length > 0 && (
              <Chip
                label={`${template.generalTags.length} Dynamic Tags`}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
            {template.constantTags && template.constantTags.length > 0 && (
              <Chip
                label={`${template.constantTags.length} Constant Tags`}
                size="small"
                variant="outlined"
                color="secondary"
              />
            )}
          </Box>
          
          <Box display="flex" gap={1} flexWrap="wrap">
            {template.generalTags && template.generalTags.slice(0, 3).map((tag, index) => (
              <Chip
                key={index}
                label={`${tag}=*`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            ))}
            {template.generalTags && template.generalTags.length > 3 && (
              <Chip
                label={`+${template.generalTags.length - 3} more`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            )}
            {template.constantTags && template.constantTags.slice(0, 3).map((tag, index) => {
              const tagKey = Object.keys(tag)[0] || '';
              const tagValue = Object.values(tag)[0] || '';
              return (
                <Chip
                  key={index}
                  label={`${tagKey}=${tagValue}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              );
            })}
            {template.constantTags && template.constantTags.length > 3 && (
              <Chip
                label={`+${template.constantTags.length - 3} more`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            )}
          </Box>
        </Box>
        
        <Box display="flex" gap={1}>
          <Tooltip title="Edit template">
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditTemplate(name, template);
              }}
            >
              <EditIcon />
            </PermissionIconButton>
          </Tooltip>
          <Tooltip title="Duplicate template">
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateTemplate(name, template);
              }}
            >
              <CopyIcon />
            </PermissionIconButton>
          </Tooltip>
          <Tooltip title="Delete template">
            <PermissionIconButton
              permission="PowerUser"
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteTemplate(name);
              }}
            >
              <DeleteIcon />
            </PermissionIconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

// Helper function to generate duplicate template names
const generateDuplicateTemplateName = (originalName: string, existingTemplates: Record<string, TagTemplate>): string => {
  let counter = 1;
  let newName = `${originalName}#duplicated`;
  
  while (existingTemplates[newName]) {
    counter++;
    newName = `${originalName}#duplicated${counter}`;
  }
  
  return newName;
};

// Helper function to check if a name is already a duplicate
const isAlreadyDuplicate = (name: string): boolean => {
  return name.includes('#duplicated');
};

export const RuleEditorEnhanced: React.FC<RuleEditorEnhancedProps> = ({ onSave }) => {
  const { showNotification } = useNotifications();
  const { user } = useAuth();
  const { isReadOnly } = usePermissions();
  
  // Enhanced state management

  const [sortBy, setSortBy] = useState<'name' | 'category' | 'created' | 'updated' | 'priority'>('priority');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- sortOrder setter kept for potential future use
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [selectedRules, setSelectedRules] = useState<string[]>([]);
  const [ruleStats, setRuleStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    byCategory: {} as Record<string, number>,
    compliance: 0,
  });

  // Professional loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Enhanced data state
  const [rules, setRules] = useState<Record<string, TagRule>>({});
  const [templates, setTemplates] = useState<Record<string, TagTemplate>>({});
  const [serviceConfig, setServiceConfig] = useState<Record<string, ServiceConfig[]>>({});
  const [onboardedRegions, setOnboardedRegions] = useState<string[]>([]);
  const [onboardedAccounts, setOnboardedAccounts] = useState<Array<{accountId: string, account_name: string}>>([]);

  // View mode state for adaptive UI
  const [viewMode, setViewMode] = useState<'compact' | 'detailed'>('compact');

  // Professional notifications

  // Enhanced wizard state
  const [ruleWizardOpen, setRuleWizardOpen] = useState(false);
  const [templateWizardOpen, setTemplateWizardOpen] = useState(false);

  const [importWizardOpen, setImportWizardOpen] = useState(false);
  
  // Import functionality state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [priorityValidationError, setPriorityValidationError] = useState<string | null>(null);
  const [importPreview, setImportPreview] = useState<any>(null);

  // Editing state
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [editingRule, setEditingRule] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);

  // Collapsible sections state
  const [rulesSectionExpanded, setRulesSectionExpanded] = useState(true);
  const [templatesSectionExpanded, setTemplatesSectionExpanded] = useState(true);

  // Professional breadcrumbs

  // Template validation state
  const [templateTagValidationErrors, setTemplateTagValidationErrors] = useState<Record<string, string[]>>({});
  const [templateDuplicateTagWarnings, setTemplateDuplicateTagWarnings] = useState<Record<number, string[]>>({});
  const [templateSimilarTagWarnings, setTemplateSimilarTagWarnings] = useState<Record<number, Array<{ ruleName: string; existingKey: string }>>>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setter kept for potential future use
  const [templateBlacklistedTagError, setTemplateBlacklistedTagError] = useState<string>('');

  // Confirmation dialog state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    type: 'rule' | 'template';
    name: string;
    onConfirm: () => void;
  }>({
    open: false,
    type: 'rule',
    name: '',
    onConfirm: () => {}
  });



  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // Loading will be shown by the loading state

      // Check if we have authentication tokens before making API calls
      const idToken = localStorage.getItem('id_token');
      const accessToken = localStorage.getItem('access_token');
      
      if (!idToken && !accessToken) {
        console.warn('No authentication tokens found, skipping API calls');
        setLoading(false);
        return;
      }

      // Force fresh fetch from backend (cache-control headers prevent caching)
      const [ruleResponse, serviceConfigResponse, accountsResponse] = await Promise.all([
        tagopsService.getRuleConfig(),
        tagopsService.getServiceConfig(),
        tagopsService.getAccounts()
      ]);

      // Parse rules data - handle both old numbered format and new direct format
      // Old format: {"rules": {"rules": {"0": {"ruleName": {...}}, "1": {...}}}}
      // New format: {"rules": {"rules": {"ruleName": {...}, "ruleName2": {...}}}}
      const rulesData: Record<string, TagRule> = {};
      
      // Get the rules object - could be at ruleResponse.rules.rules or ruleResponse.rules
      const rulesObj = ruleResponse.rules?.rules || ruleResponse.rules;
      
      if (rulesObj && typeof rulesObj === 'object') {
        // Check if it's the old numbered format (keys are numbers)
        const firstKey = Object.keys(rulesObj)[0];
        const isNumberedFormat = firstKey && /^\d+$/.test(firstKey);
        
        if (isNumberedFormat) {
          // Old numbered format: {"0": {"ruleName": {...}}, "1": {...}}
          Object.entries(rulesObj).forEach(([ruleGroup, ruleGroupData]) => {
            if (typeof ruleGroupData === 'object' && ruleGroupData !== null) {
              Object.entries(ruleGroupData as Record<string, TagRule>).forEach(([ruleName, ruleData]) => {
                // Only process valid rule objects (skip metadata and other properties)
                if (ruleData && typeof ruleData === 'object' && (ruleData.rule || ruleData.name || ruleData.condition)) {
                  const ruleWithName = {
                    ...ruleData,
                    name: ruleName
                  };
                  rulesData[ruleName] = ruleWithName;
                }
              });
            }
          });
        } else {
          // New direct format: {"ruleName": {...}, "ruleName2": {...}}
          Object.entries(rulesObj).forEach(([ruleName, ruleData]: [string, any]) => {
            // Only process valid rule objects (skip metadata and other properties)
            if (ruleData && typeof ruleData === 'object' && (ruleData.rule || ruleData.name || ruleData.condition)) {
              const ruleWithName = {
                ...ruleData,
                name: ruleName
              };
              rulesData[ruleName] = ruleWithName;
            }
          });
        }
      }
      
      // Ensure all rules have priorities assigned
      // Sort by existing priority first, then assign missing priorities
      const rulesWithPriorities = Object.entries(rulesData);
      const rulesWithPriority = rulesWithPriorities.filter(([, rule]) => rule.priority !== undefined && rule.priority !== null);
      const rulesWithoutPriority = rulesWithPriorities.filter(([, rule]) => rule.priority === undefined || rule.priority === null);
      
      // Sort rules with priority
      rulesWithPriority.sort(([, a], [, b]) => (a.priority ?? 9999) - (b.priority ?? 9999));
      
      // Assign priorities to rules without them
      let nextPriority = rulesWithPriority.length > 0 
        ? Math.max(...rulesWithPriority.map(([, rule]) => rule.priority ?? 0)) + 1
        : 1;
      
      rulesWithoutPriority.forEach(([ruleName, ruleData]) => {
        rulesData[ruleName] = {
          ...ruleData,
          priority: nextPriority++
        };
      });
      
      setRules(rulesData);
      
      // Parse templates data - handle both array format and object format
      // Old format: {"templates": [{"templateName": {...}}]}
      // New format: {"templates": {"templateName": {...}}}
      const templatesData: Record<string, TagTemplate> = {};
      const templatesSource = ruleResponse.rules?.templates;
      
      if (templatesSource) {
        if (Array.isArray(templatesSource)) {
          // Old array format
          templatesSource.forEach((templateObj: any) => {
            if (typeof templateObj === 'object' && templateObj !== null) {
              Object.entries(templateObj).forEach(([templateName, templateData]) => {
                templatesData[templateName] = templateData as TagTemplate;
              });
            }
          });
        } else if (typeof templatesSource === 'object') {
          // New object format
          Object.entries(templatesSource).forEach(([templateName, templateData]) => {
            templatesData[templateName] = templateData as TagTemplate;
          });
        }
      }
      setTemplates(templatesData);
      
      // Parse service config
      const serviceConfigData = serviceConfigResponse?.services || {};
      setServiceConfig(serviceConfigData);
      
      // Parse regions
      const regionsData = serviceConfigResponse?.onboarded_regions || [];
      setOnboardedRegions(regionsData);
      
      // Parse accounts - try different possible structures
      let accountsData = [];
      if (accountsResponse?.data?.accounts) {
        accountsData = accountsResponse.data.accounts;
      } else if (accountsResponse?.accounts) {
        accountsData = accountsResponse.accounts;
      } else if (Array.isArray(accountsResponse?.data)) {
        accountsData = accountsResponse.data;
      } else if (Array.isArray(accountsResponse)) {
        accountsData = accountsResponse;
      }
      
      setOnboardedAccounts(accountsData);

      } catch (error) {
        console.error('Error loading data:', error);
        showNotification(createErrorNotification(error, undefined, 'Failed to load configuration'));
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  const calculateStats = useCallback(() => {
    const stats = {
      total: Object.keys(rules).length,
      active: Object.values(rules).filter(rule => rule.enabled !== false).length,
      inactive: Object.values(rules).filter(rule => rule.enabled === false).length,
      byCategory: {} as Record<string, number>,
      compliance: 0,
    };

    // Calculate category distribution
    Object.values(rules).forEach(rule => {
      const category = rule.category || 'uncategorized';
      stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
    });

    // Calculate compliance score
    const totalRules = stats.total;
    const compliantRules = Object.values(rules).filter(rule => {
      // Add your compliance logic here
      return true; // Placeholder
    }).length;
    stats.compliance = totalRules > 0 ? Math.round((compliantRules / totalRules) * 100) : 0;

    setRuleStats(stats);
  }, [rules]);

  // Load data on component mount, but only when user is authenticated
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [loadData, user]);

  // Recalculate stats when rules change
  useEffect(() => {
    calculateStats();
  }, [rules, calculateStats]);

      const handleBulkAction = async (action: string) => {
      if (selectedRules.length === 0) {
        showNotification(createErrorNotification(new Error('Please select rules to perform bulk action')));
        return;
      }

      setSaving(true);
      try {
        const updatedRules = { ...rules };
        
        switch (action) {
          case 'enable':
            // Enable selected rules
            selectedRules.forEach(ruleName => {
              if (updatedRules[ruleName]) {
                updatedRules[ruleName] = { ...updatedRules[ruleName], enabled: true };
              }
            });
            setRules(updatedRules);
            showNotification(createSuccessNotification('Success', `${selectedRules.length} rules enabled`));
            break;
            
          case 'disable':
            // Disable selected rules
            selectedRules.forEach(ruleName => {
              if (updatedRules[ruleName]) {
                updatedRules[ruleName] = { ...updatedRules[ruleName], enabled: false };
              }
            });
            setRules(updatedRules);
            showNotification(createSuccessNotification('Success', `${selectedRules.length} rules disabled`));
            break;
            
          case 'duplicate':
            // Check if any selected rules are already duplicates
            const alreadyDuplicatedRules = selectedRules.filter(ruleName => isAlreadyDuplicate(ruleName));
            if (alreadyDuplicatedRules.length > 0) {
              showNotification(createErrorNotification(
                new Error('Cannot duplicate already duplicated rules'), 
                undefined, 
                `Please rename the following rules first: ${alreadyDuplicatedRules.join(', ')}`
              ));
              setSaving(false);
              return;
            }

            // Duplicate selected rules
            const duplicatedRules: Record<string, TagRule> = {};
            selectedRules.forEach(ruleName => {
              if (updatedRules[ruleName]) {
                const originalRule = updatedRules[ruleName];
                const newName = generateDuplicateRuleName(ruleName, { ...updatedRules, ...duplicatedRules });
                duplicatedRules[newName] = {
                  ...originalRule,
                  name: newName,
                  enabled: true // Enable duplicated rules by default
                };
              }
            });
            
            // Add duplicated rules to the existing rules
            Object.entries(duplicatedRules).forEach(([name, rule]) => {
              updatedRules[name] = rule;
            });
            
            setRules(updatedRules);
            showNotification(createSuccessNotification('Success', `${selectedRules.length} rules duplicated`));
            break;
            
          case 'delete':
            // Delete selected rules
            selectedRules.forEach(ruleName => {
              delete updatedRules[ruleName];
            });
            setRules(updatedRules);
            showNotification(createSuccessNotification('Success', `${selectedRules.length} rules deleted`));
            break;
            
          default:
            break;
        }
        
        // Save to backend
        await handleSaveRules(updatedRules);
        
        setSelectedRules([]);
      } catch (error) {
        showNotification(createErrorNotification(error, undefined, 'Bulk action failed'));
      } finally {
        setSaving(false);
      }
    };

  // Helper function to save multiple rules to backend
  const handleSaveRules = async (updatedRules: Record<string, TagRule>) => {
    try {
      // Format data for API - use direct format (no numbered keys)
      const apiRulesData: Record<string, TagRule> = {};
      Object.entries(updatedRules).forEach(([ruleName, ruleData]) => {
        // Remove the name from the rule data since it's the key
        const { name, ...ruleWithoutName } = ruleData;
        apiRulesData[ruleName] = ruleWithoutName;
      });
      
      // Format templates for API - use object format (no array)
      const apiTemplatesData: Record<string, TagTemplate> = {};
      Object.entries(templates).forEach(([templateName, templateData]) => {
        apiTemplatesData[templateName] = templateData;
      });
      
      // Prepare the full API payload
      const apiPayload: any = {
        startRule: "0", // Default start rule
        templates: apiTemplatesData, // Object format
        rules: apiRulesData // Direct format (no numbered keys)
      };
      
      // Call the API to save
      await tagopsService.updateRuleConfig(apiPayload);
      
      // Call the onSave callback to notify parent component
      onSave(apiPayload);
    } catch (error) {
      console.error('Error saving rules:', error);
      throw error; // Re-throw to be handled by caller
    }
  };

  // Handle individual rule duplication
  const handleDuplicateRule = async (ruleName: string, rule: TagRule) => {
    // Check if the rule is already a duplicate
    if (isAlreadyDuplicate(ruleName)) {
      showNotification(createErrorNotification(
        new Error('Cannot duplicate a duplicated rule'), 
        undefined, 
        'Please rename the rule first before duplicating it'
      ));
      return;
    }

    setSaving(true);
    try {
      const newName = generateDuplicateRuleName(ruleName, rules);
      const duplicatedRule = {
        ...rule,
        name: newName,
        enabled: true // Enable duplicated rule by default
      };
      
      const updatedRules = { ...rules, [newName]: duplicatedRule };
      setRules(updatedRules);
      
      // Save to backend
      await handleSaveRules(updatedRules);
      
      showNotification(createSuccessNotification('Success', `Rule "${ruleName}" duplicated as "${newName}"`));
    } catch (error) {
      showNotification(createErrorNotification(error, undefined, 'Failed to duplicate rule'));
    } finally {
      setSaving(false);
    }
  };

  // Handle individual rule enable/disable toggle
  const handleToggleRuleStatus = async (ruleName: string, rule: TagRule) => {
    setSaving(true);
    try {
      const updatedRule = { ...rule, enabled: !rule.enabled };
      const updatedRules = { ...rules, [ruleName]: updatedRule };
      setRules(updatedRules);
      
      // Save to backend
      await handleSaveRules(updatedRules);
      
      const status = updatedRule.enabled ? 'enabled' : 'disabled';
      showNotification(createSuccessNotification('Success', `Rule "${ruleName}" ${status}`));
    } catch (error) {
      showNotification(createErrorNotification(error, undefined, 'Failed to toggle rule status'));
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    const exportData = {
      rules,
      templates,
      metadata: {
        exportedAt: new Date().toISOString(),
        version: '1.0',
        totalRules: Object.keys(rules).length,
        totalTemplates: Object.keys(templates).length,
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TagOps-rules-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);

    showNotification(createSuccessNotification('Success', 'Rules exported successfully'));
  };


   const handleEditRule = (name: string, rule: TagRule) => {
     setEditingRule(name);
     setRuleWizardOpen(true);
   };

       const handleEditTemplate = (name: string, template: TagTemplate) => {
      // Set template data for editing
      setTemplateData({
        name: name,
        description: template.description || '',
        category: template.category || '',
        generalTags: template.generalTags || [],
        constantTags: template.constantTags || []
      });
      setEditingTemplate(name);
      setTemplateDuplicateTagWarnings({});
      setTemplateSimilarTagWarnings({});
      setTemplateWizardOpen(true);
    };

    // Delete confirmation functions
    const openDeleteConfirmation = (type: 'rule' | 'template', name: string, onConfirm: () => void) => {
      setDeleteConfirmation({
        open: true,
        type,
        name,
        onConfirm
      });
    };

    const closeDeleteConfirmation = () => {
      setDeleteConfirmation({
        open: false,
        type: 'rule',
        name: '',
        onConfirm: () => {}
      });
    };

    const handleDeleteRule = async (ruleName: string) => {
      const performDelete = async () => {
        setSaving(true);
        try {
          // Remove the rule from local state
          const { [ruleName]: removed, ...updatedRules } = rules;
          setRules(updatedRules);
          
          // Format rules for API - use direct format (no numbered keys)
          const apiRulesData: Record<string, TagRule> = {};
          Object.entries(updatedRules).forEach(([ruleName, ruleData]) => {
            // Remove the name from the rule data since it's the key
            const { name, ...ruleWithoutName } = ruleData;
            apiRulesData[ruleName] = ruleWithoutName;
          });
          
          // Format templates for API - use object format (no array)
          const apiTemplatesData: Record<string, TagTemplate> = {};
          Object.entries(templates).forEach(([templateName, templateData]) => {
            apiTemplatesData[templateName] = templateData;
          });
          
          // Prepare the full API payload
          const apiPayload: any = {
            startRule: "0", // Default start rule
            templates: apiTemplatesData, // Object format
            rules: apiRulesData // Direct format (no numbered keys)
          };
          
          // Call the API to save
          await tagopsService.updateRuleConfig(apiPayload);
          
          showNotification(createSuccessNotification('Success', `Rule "${ruleName}" deleted successfully`));
        } catch (error) {
          console.error('Error deleting rule:', error);
          showNotification(createErrorNotification(error, undefined, 'Failed to delete rule'));
          // Revert the change if API call fails
          setRules(rules);
        } finally {
          setSaving(false);
          closeDeleteConfirmation();
        }
      };

      openDeleteConfirmation('rule', ruleName, performDelete);
    };

    const handleDeleteTemplate = async (templateName: string) => {
      // Check if template is in use by any rules
      const rulesUsingTemplate = Object.entries(rules).filter(
        ([, rule]) => rule.rule?.type === 'template' && rule.rule?.templateName === templateName
      ).map(([ruleName]) => ruleName);

      if (rulesUsingTemplate.length > 0) {
        const ruleList = rulesUsingTemplate.join(', ');
        showNotification(createErrorNotification(
          new Error(`Cannot delete template: Template is in use`),
          undefined,
          `Cannot delete template "${templateName}": It is currently in use by the following rule(s): ${ruleList}. Remove the template from these rules before deleting.`
        ));
        return;
      }

      const performDelete = async () => {
        setSaving(true);
        try {
          // Remove the template from local state
          const { [templateName]: removed, ...updatedTemplates } = templates;
          setTemplates(updatedTemplates);
          
          // Format templates for API - use object format (no array)
          const apiTemplatesData: Record<string, TagTemplate> = {};
          Object.entries(updatedTemplates).forEach(([templateName, templateData]) => {
            apiTemplatesData[templateName] = templateData;
          });

          // Format rules for API - use direct format (no numbered keys)
          const apiRulesData: Record<string, TagRule> = {};
          Object.entries(rules).forEach(([ruleName, ruleData]) => {
            // Remove the name from the rule data since it's the key
            const { name, ...ruleWithoutName } = ruleData;
            apiRulesData[ruleName] = ruleWithoutName;
          });
          
          // Prepare the full API payload
          const apiPayload: any = {
            startRule: "0", // Default start rule
            templates: apiTemplatesData, // Object format
            rules: apiRulesData // Direct format (no numbered keys)
          };
          
          // Call the API to save
                     await tagopsService.updateRuleConfig(apiPayload);
           
           showNotification(createSuccessNotification('Success', `Template "${templateName}" deleted successfully`));
         } catch (error) {
           console.error('Error deleting template:', error);
           showNotification(createErrorNotification(error, undefined, 'Failed to delete template'));
          // Revert the change if API call fails
          setTemplates(templates);
        } finally {
          setSaving(false);
          closeDeleteConfirmation();
        }
      };

      openDeleteConfirmation('template', templateName, performDelete);
    };

    // Handle individual template duplication
    const handleDuplicateTemplate = async (templateName: string, template: TagTemplate) => {
      // Check if the template is already a duplicate
      if (isAlreadyDuplicate(templateName)) {
        showNotification(createErrorNotification(
          new Error('Cannot duplicate a duplicated template'), 
          undefined, 
          'Please rename the template first before duplicating it'
        ));
        return;
      }

      setSaving(true);
      try {
        const newName = generateDuplicateTemplateName(templateName, templates);
        const duplicatedTemplate = {
          ...template
        };
        
        const updatedTemplates = { ...templates, [newName]: duplicatedTemplate };
        setTemplates(updatedTemplates);
        
          // Format templates for API - use object format (no array)
          const apiTemplatesData: Record<string, TagTemplate> = {};
          Object.entries(updatedTemplates).forEach(([templateName, templateData]) => {
            apiTemplatesData[templateName] = templateData;
          });

        // Format rules for API - use direct format (no numbered keys)
        const apiRulesData: Record<string, TagRule> = {};
        Object.entries(rules).forEach(([ruleName, ruleData]) => {
          // Remove the name from the rule data since it's the key
          const { name, ...ruleWithoutName } = ruleData;
          apiRulesData[ruleName] = ruleWithoutName;
        });
        
        // Format templates for API - use object format (no array)
        const apiTemplatesDataObj: Record<string, TagTemplate> = {};
        Object.entries(templates).forEach(([templateName, templateData]) => {
          apiTemplatesDataObj[templateName] = templateData;
        });
        
        // Prepare the full API payload
        const apiPayload: any = {
          startRule: "0", // Default start rule
          templates: apiTemplatesDataObj, // Object format
          rules: apiRulesData // Direct format (no numbered keys)
        };
        
        // Call the API to save
        await tagopsService.updateRuleConfig(apiPayload);
        onSave(apiPayload);
        
        showNotification(createSuccessNotification('Success', `Template "${templateName}" duplicated as "${newName}"`));
      } catch (error) {
        console.error('Error duplicating template:', error);
        showNotification(createErrorNotification(error, undefined, 'Failed to duplicate template'));
        // Revert the change if API call fails
        setTemplates(templates);
      } finally {
        setSaving(false);
      }
    };

     const handleSaveRule = async (rule: TagRule) => {
       // Assign priority to new rules if not set
       if (!rule.priority || rule.priority === 1) {
         const ruleName = rule.name || '';
         const isNewRule = !editingRule || !rules[editingRule] || editingRule !== ruleName;
         if (isNewRule) {
           rule.priority = getNextPriority(rules);
         }
       }
    setSaving(true);
    try {
      let updatedRules;
      
      if (editingRule) {
        // Editing existing rule
        const newRuleName = rule.name || editingRule;
        
        // Remove the old rule entry if name changed
        if (newRuleName !== editingRule) {
          const { [editingRule]: removed, ...rulesWithoutOld } = rules;
          updatedRules = { ...rulesWithoutOld, [newRuleName]: rule };
        } else {
          // Name didn't change, just update the existing rule
          updatedRules = { ...rules, [editingRule]: rule };
        }
      } else {
        // Creating new rule
        const ruleName = rule.name || `rule_${Date.now()}`;
        updatedRules = { ...rules, [ruleName]: rule };
      }
      
      setRules(updatedRules);
      
      // Format templates for API - use object format (no array)
      const apiTemplatesData: Record<string, TagTemplate> = {};
      Object.entries(templates).forEach(([templateName, templateData]) => {
        apiTemplatesData[templateName] = templateData;
      });
      
      // Format rules for API - use direct format (no numbered keys)
      const apiRulesData: Record<string, TagRule> = {};
      Object.entries(updatedRules).forEach(([ruleName, ruleData]) => {
        // Remove the name from the rule data since it's the key
        const { name, ...ruleWithoutName } = ruleData;
        apiRulesData[ruleName] = ruleWithoutName;
      });
      
      // Prepare the full API payload
      const apiPayload: any = {
        startRule: "0", // Default start rule
        templates: apiTemplatesData, // Object format
        rules: apiRulesData // Direct format (no numbered keys)
      };
      
      // Call the API to save
             await tagopsService.updateRuleConfig(apiPayload);
       
       showNotification(createSuccessNotification('Success', editingRule ? 'Rule updated successfully' : 'Rule saved successfully'));
       setRuleWizardOpen(false);
       setEditingRule(null); // Clear editing state
     } catch (error) {
       console.error('Error saving rule:', error);
       showNotification(createErrorNotification(error, undefined, 'Failed to save rule'));
    } finally {
      setSaving(false);
    }
  };

  // Template wizard functions
  const [templateWizardStep, setTemplateWizardStep] = useState(0);
  const [templateData, setTemplateData] = useState({
    name: '',
    description: '',
    category: '',
    generalTags: [] as string[],
    constantTags: [] as Array<Record<string, string>>,
  });
  // Template validation
  const [templateValidationErrors, setTemplateValidationErrors] = useState<Record<string, string[]>>({});
  const [templateStepErrors, setTemplateStepErrors] = useState<Record<number, string[]>>({});
  
  
  const isTemplateValid = useMemo(() => {
    return templateData.name.trim() !== '' && 
           templateData.category !== '' &&
           (templateData.generalTags.length > 0 || templateData.constantTags.length > 0);
  }, [templateData]);
  const [templateSaving, setTemplateSaving] = useState(false);

  const getTemplateStepStatus = (index: number): 'completed' | 'current' | 'error' | 'upcoming' => {
    const hasErrors = templateStepErrors[index]?.length > 0;
    const isCompleted = templateWizardStep > index;
    const isCurrent = templateWizardStep === index;
    
    if (hasErrors) return 'error';
    if (isCompleted) return 'completed';
    if (isCurrent) return 'current';
    return 'upcoming';
  };

  const templateSteps: StepType[] = [
    {
      id: 'basic',
      label: 'Basic Information',
      description: 'Define template name and description',
      status: getTemplateStepStatus(0),
      icon: <Info className="w-6 h-6" />,
    },
    {
      id: 'dynamic',
      label: 'Dynamic Tags',
      description: 'Add tags that users will define values for (optional)',
      status: getTemplateStepStatus(1),
      icon: <ListChecks className="w-6 h-6" />,
    },
    {
      id: 'constant',
      label: 'Constant Tags',
      description: 'Add tags with fixed values (optional)',
      status: getTemplateStepStatus(2),
      icon: <Zap className="w-6 h-6" />,
    },
    {
      id: 'review',
      label: 'Review & Save',
      description: 'Review your template configuration',
      status: getTemplateStepStatus(3),
      icon: <CheckCircle className="w-6 h-6" />,
    },
  ];

  const handleTemplateWizardNext = () => {
    if (templateWizardStep < templateSteps.length - 1) {
      setTemplateWizardStep(templateWizardStep + 1);
    }
  };

  const handleTemplateWizardBack = () => {
    if (templateWizardStep > 0) {
      setTemplateWizardStep(templateWizardStep - 1);
    }
  };

  const handleSaveTemplate = async () => {
    if (!isTemplateValid) {
      showNotification(createErrorNotification(new Error('Please complete all required fields')));
      return;
    }

    // Validate all template tags before saving
    const currentTags = templateData.constantTags || [];
    let hasTagErrors = false;
    
    currentTags.forEach((tag, index) => {
      const tagKey = Object.keys(tag)[0] || '';
      const tagValue = Object.values(tag)[0] || '';
      const isValid = validateTemplateTag(tagKey, tagValue, index);
      if (!isValid) {
        hasTagErrors = true;
      }
    });

    if (hasTagErrors) {
      showNotification(createErrorNotification(new Error('Please fix tag validation errors before saving')));
      return;
    }

    setTemplateSaving(true);
    try {
      // Create the template object
      const newTemplate: TagTemplate = {
        name: templateData.name,
        description: templateData.description,
        category: templateData.category,
        generalTags: templateData.generalTags,
        constantTags: templateData.constantTags,
      };

      // Add to templates (update if editing, create if new)
      let newTemplates;
      
      if (editingTemplate) {
        // Editing existing template
        const newTemplateName = templateData.name.trim();
        
        // Remove the old template entry if name changed
        if (newTemplateName !== editingTemplate) {
          const { [editingTemplate]: removed, ...templatesWithoutOld } = templates;
          newTemplates = { ...templatesWithoutOld, [newTemplateName]: newTemplate };
        } else {
          // Name didn't change, just update the existing template
          newTemplates = { ...templates, [editingTemplate]: newTemplate };
        }
      } else {
        // Creating new template
        const templateName = templateData.name.trim();
        newTemplates = { ...templates, [templateName]: newTemplate };
      }
      
      setTemplates(newTemplates);

      // Format templates for API - use object format (no array)
      const apiTemplatesData: Record<string, TagTemplate> = {};
      Object.entries(newTemplates).forEach(([templateName, templateData]) => {
        apiTemplatesData[templateName] = templateData;
      });

      // Format rules for API - use direct format (no numbered keys)
      const apiRulesData: Record<string, TagRule> = {};
      Object.entries(rules).forEach(([ruleName, ruleData]) => {
        // Remove the name from the rule data since it's the key
        const { name, ...ruleWithoutName } = ruleData;
        apiRulesData[ruleName] = ruleWithoutName;
      });
      
      // Format templates for API - use object format (no array)
      const apiTemplatesDataObj: Record<string, TagTemplate> = {};
      Object.entries(templates).forEach(([templateName, templateData]) => {
        apiTemplatesDataObj[templateName] = templateData;
      });
      
      // Prepare the full API payload
      const apiPayload: any = {
        startRule: "0", // Default start rule
        templates: apiTemplatesDataObj, // Object format
        rules: apiRulesData // Direct format (no numbered keys)
      };
      
      // Call the API to save
      await tagopsService.updateRuleConfig(apiPayload);

      // Reset form
      setTemplateData({
        name: '',
        description: '',
        category: '',
        generalTags: [],
        constantTags: []
      });
      setTemplateWizardStep(0);
      setTemplateWizardOpen(false);
      setEditingTemplate(null);

      showNotification(createSuccessNotification('Success', editingTemplate ? 'Template updated successfully' : 'Template created successfully'));
    } catch (error) {
      console.error('Error saving template:', error);
      showNotification(createErrorNotification(error, undefined, 'Failed to save template'));
    } finally {
      setTemplateSaving(false);
    }
  };

  const updateTemplateData = (updates: Partial<typeof templateData>) => {
    setTemplateData(prev => ({ ...prev, ...updates }));
  };

  // Tag validation helper
  const validateTagCharacters = (tagKey: string, tagValue: string): string[] => {
    const errors: string[] = [];
    const validChars = /^[a-zA-Z0-9\s.:/=+\-@]*$/;
    
    if (!validChars.test(tagKey)) {
      errors.push(`Tag key "${tagKey}" contains invalid characters. Only letters, numbers, spaces, and . : / = + - @ are allowed`);
    }
    
    if (!validChars.test(tagValue)) {
      errors.push(`Tag value "${tagValue}" contains invalid characters. Only letters, numbers, spaces, and . : / = + - @ are allowed`);
    }
    
    return errors;
  };

  // Validate rule priorities
  const validateRulePriorities = (rules: any): { isValid: boolean; error: string | null } => {
    if (!rules || typeof rules !== 'object') {
      return { isValid: true, error: null }; // Let other validation handle this
    }

    const priorities: number[] = [];
    const priorityToRuleNames: Record<number, string[]> = {};

    // Collect all priorities
    Object.entries(rules).forEach(([ruleName, ruleData]: [string, any]) => {
      if (ruleData && typeof ruleData === 'object' && typeof ruleData.priority === 'number') {
        const priority = ruleData.priority;
        priorities.push(priority);
        
        if (!priorityToRuleNames[priority]) {
          priorityToRuleNames[priority] = [];
        }
        priorityToRuleNames[priority].push(ruleName);
      }
    });

    if (priorities.length === 0) {
      return { isValid: true, error: null }; // No priorities to validate
    }

    // Check for duplicate priorities
    const duplicates: number[] = [];
    Object.entries(priorityToRuleNames).forEach(([priority, ruleNames]) => {
      if (ruleNames.length > 1) {
        duplicates.push(Number(priority));
      }
    });

    if (duplicates.length > 0) {
      const duplicateDetails = duplicates.map(priority => {
        const ruleNames = priorityToRuleNames[priority];
        return `Priority ${priority} is used by: ${ruleNames.join(', ')}`;
      }).join('; ');
      return { isValid: false, error: `Duplicate priorities found: ${duplicateDetails}` };
    }

    // Check for sequential priorities without gaps
    const sortedPriorities = [...priorities].sort((a, b) => a - b);
    const minPriority = sortedPriorities[0];
    const maxPriority = sortedPriorities[sortedPriorities.length - 1];
    const expectedCount = maxPriority - minPriority + 1;

    if (sortedPriorities.length !== expectedCount) {
      // Find missing priorities
      const missingPriorities: number[] = [];
      for (let i = minPriority; i <= maxPriority; i++) {
        if (!priorities.includes(i)) {
          missingPriorities.push(i);
        }
      }
      
      if (missingPriorities.length > 0) {
        return { 
          isValid: false, 
          error: `Missing priorities: ${missingPriorities.join(', ')}. Priorities must be sequential (e.g., 1, 2, 3, ...) without gaps.` 
        };
      }
    }

    return { isValid: true, error: null };
  };

  // Import validation functions
  const validateRulesStructure = (rules: any): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!rules || typeof rules !== 'object') {
      errors.push('Rules must be an object');
      return { isValid: false, errors };
    }

    Object.entries(rules).forEach(([ruleName, ruleData]: [string, any]) => {
      if (!ruleData || typeof ruleData !== 'object') {
        errors.push(`Rule "${ruleName}" must be an object`);
        return;
      }

      // Check required fields
      if (!ruleData.name) {
        errors.push(`Rule "${ruleName}" is missing required field: name`);
      }
      if (!ruleData.rule) {
        errors.push(`Rule "${ruleName}" is missing required field: rule`);
      } else if (!ruleData.rule.type) {
        errors.push(`Rule "${ruleName}" is missing required field: rule.type`);
      }
      if (!ruleData.condition) {
        errors.push(`Rule "${ruleName}" is missing required field: condition`);
      } else if (!Array.isArray(ruleData.condition)) {
        errors.push(`Rule "${ruleName}" condition must be an array`);
      }

      // Validate rule type
      if (ruleData.rule && ruleData.rule.type) {
        if (!['add', 'remove', 'template'].includes(ruleData.rule.type)) {
          errors.push(`Rule "${ruleName}" has invalid rule type: ${ruleData.rule.type}. Must be 'add', 'remove', or 'template'`);
        }
        
        // Validate template rule
        if (ruleData.rule.type === 'template' && !ruleData.rule.templateName) {
          errors.push(`Rule "${ruleName}" with type 'template' is missing templateName`);
        }

        // Validate tags for add/remove rules
        if (ruleData.rule.type === 'add' || ruleData.rule.type === 'remove') {
          if (ruleData.rule.tags && Array.isArray(ruleData.rule.tags)) {
            ruleData.rule.tags.forEach((tag: any, index: number) => {
              if (tag && typeof tag === 'object') {
                Object.entries(tag).forEach(([key, value]) => {
                  const tagErrors = validateTagCharacters(key, String(value));
                  tagErrors.forEach(error => {
                    errors.push(`Rule "${ruleName}" tag ${index + 1}: ${error}`);
                  });
                });
              }
            });
          }
        }
      }

      // Validate condition structure
      if (ruleData.condition && Array.isArray(ruleData.condition)) {
        ruleData.condition.forEach((condition: any, index: number) => {
          if (!condition.type) {
            errors.push(`Rule "${ruleName}" condition ${index + 1} is missing type`);
          }
          if (!condition.operator) {
            errors.push(`Rule "${ruleName}" condition ${index + 1} is missing operator`);
          }
          if (!condition.value) {
            errors.push(`Rule "${ruleName}" condition ${index + 1} is missing value`);
          }
        });
      }
    });

    return { isValid: errors.length === 0, errors };
  };

  const validateTemplatesStructure = (templates: any): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!templates || typeof templates !== 'object') {
      errors.push('Templates must be an object');
      return { isValid: false, errors };
    }

    Object.entries(templates).forEach(([templateName, templateData]: [string, any]) => {
      if (!templateData || typeof templateData !== 'object') {
        errors.push(`Template "${templateName}" must be an object`);
        return;
      }

      // Check required fields
      if (!templateData.name) {
        errors.push(`Template "${templateName}" is missing required field: name`);
      }

      // Validate generalTags
      if (templateData.generalTags && !Array.isArray(templateData.generalTags)) {
        errors.push(`Template "${templateName}" generalTags must be an array`);
      }

      // Validate constantTags
      if (templateData.constantTags && !Array.isArray(templateData.constantTags)) {
        errors.push(`Template "${templateName}" constantTags must be an array`);
      } else if (templateData.constantTags && Array.isArray(templateData.constantTags)) {
        templateData.constantTags.forEach((tag: any, index: number) => {
          if (!tag || typeof tag !== 'object') {
            errors.push(`Template "${templateName}" constantTag ${index + 1} must be an object`);
          } else {
            const tagKeys = Object.keys(tag);
            if (tagKeys.length === 0) {
              errors.push(`Template "${templateName}" constantTag ${index + 1} is empty`);
            }
            tagKeys.forEach(key => {
              if (!tag[key] || typeof tag[key] !== 'string') {
                errors.push(`Template "${templateName}" constantTag ${index + 1} has invalid value for key "${key}"`);
              } else {
                // Validate tag characters
                const tagErrors = validateTagCharacters(key, tag[key]);
                tagErrors.forEach(error => {
                  errors.push(`Template "${templateName}" constantTag ${index + 1}: ${error}`);
                });
              }
            });
          }
        });
      }
    });

    return { isValid: errors.length === 0, errors };
  };

  // Import functionality handlers
  const handleDownloadTemplate = async () => {
    try {
      // Create a comprehensive template with examples
      const templateConfig = {
        rules: {
          "example-sqs-rule": {
            "description": "Example rule for SQS queues",
            "category": "cost-management",
            "enabled": true,
            "priority": 1,
            "rule": {
              "type": "add",
              "tags": [
                {
                  "Environment": "production"
                },
                {
                  "CostCenter": "engineering"
                },
                {
                  "Owner": "devops-team"
                },
                {
                  "Project": "main-platform"
                },
                {
                  "Region": "us-east-1"
                }
              ]
            },
            "condition": [
              {
                "type": "resourceType",
                "operator": "equal",
                "value": "sqs:queue"
              }
            ],
            "conditionOperation": "single",
            "name": "example-sqs-rule"
          },
          "example-logs-rule": {
            "description": "Example rule for CloudWatch log groups",
            "category": "operations",
            "enabled": true,
            "priority": 2,
            "rule": {
              "type": "add",
              "tags": [
                {
                  "LogRetention": "30-days"
                },
                {
                  "Monitoring": "enabled"
                }
              ]
            },
            "condition": [
              {
                "type": "resourceType",
                "operator": "equal",
                "value": "logs:log-group"
              }
            ],
            "conditionOperation": "single",
            "name": "example-logs-rule"
          },
          "example-template-rule": {
            "description": "Example rule using a template",
            "category": "security-compliance",
            "enabled": true,
            "priority": 3,
            "rule": {
              "type": "template",
              "tags": [],
              "templateName": "security-template"
            },
            "condition": [
              {
                "type": "resourceType",
                "operator": "equal",
                "value": "ec2:security-group"
              }
            ],
            "conditionOperation": "single",
            "name": "example-template-rule"
          }
        },
        templates: {
          "security-template": {
            "name": "security-template",
            "description": "Standard security tags for resources",
            "category": "security-compliance",
            "generalTags": [
              "created-by",
              "creation-date",
              "region",
              "environment"
            ],
            "constantTags": [
              {
                "SecurityLevel": "high"
              },
              {
                "Compliance": "required"
              },
              {
                "DataClassification": "confidential"
              }
            ]
          },
          "cost-management-template": {
            "name": "cost-management-template",
            "description": "Standard cost management tags",
            "category": "cost-management",
            "generalTags": [
              "created-by",
              "creation-date",
              "costCenter"
            ],
            "constantTags": [
              {
                "Budget": "engineering"
              },
              {
                "Project": "main-platform"
              }
            ]
          }
        },
        metadata: {
          "exportedAt": new Date().toISOString(),
          "version": "1.0",
          "totalRules": 3,
          "totalTemplates": 2,
          "description": "Example rule configuration template with sample rules and templates"
        }
      };

      const blob = new Blob([JSON.stringify(templateConfig, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ruleconfig-template.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showNotification(createSuccessNotification('Template Downloaded', 'Example rule configuration template downloaded successfully'));
    } catch (error) {
      console.error('Error downloading template:', error);
      showNotification(createErrorNotification(error, undefined, 'Failed to download template'));
    }
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const fileInput = event.target;
    
    if (!file) {
      // Reset file input to allow selecting the same file again
      if (fileInput) {
        fileInput.value = '';
      }
      return;
    }

    setSelectedFile(file);
    setImportError(null);
    setImportSuccess(null);
    setImportPreview(null);
    setPriorityValidationError(null);

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.json')) {
      setImportError('Please select a valid JSON file');
      // Reset file input to allow selecting the same file again
      fileInput.value = '';
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setImportError('File size must be less than 5MB');
      // Reset file input to allow selecting the same file again
      fileInput.value = '';
      return;
    }

    // Read and validate file content
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsedConfig = JSON.parse(content);
        
        // Validate the structure
        if (!parsedConfig || typeof parsedConfig !== 'object') {
          setImportError('Invalid JSON structure');
          // Reset file input to allow selecting the same file again
          fileInput.value = '';
          return;
        }

        // Check for required fields - accept both formats
        if (!parsedConfig.rules && !parsedConfig.templates) {
          setImportError('Invalid configuration: missing rules or templates');
          // Reset file input to allow selecting the same file again
          fileInput.value = '';
          return;
        }

        // Validate that rules and templates are objects (not arrays)
        if (parsedConfig.rules && !Array.isArray(parsedConfig.rules) && typeof parsedConfig.rules !== 'object') {
          setImportError('Invalid rules format: rules must be an object');
          // Reset file input to allow selecting the same file again
          fileInput.value = '';
          return;
        }

        if (parsedConfig.templates && !Array.isArray(parsedConfig.templates) && typeof parsedConfig.templates !== 'object') {
          setImportError('Invalid templates format: templates must be an object or array');
          // Reset file input to allow selecting the same file again
          fileInput.value = '';
          return;
        }

        // Validate rules structure
        if (parsedConfig.rules) {
          const ruleValidation = validateRulesStructure(parsedConfig.rules);
          if (!ruleValidation.isValid) {
            setImportError(`Invalid rules structure: ${ruleValidation.errors.join(', ')}`);
            setPriorityValidationError(null);
            // Reset file input to allow selecting the same file again
            fileInput.value = '';
            return;
          }

          // Validate rule priorities
          const priorityValidation = validateRulePriorities(parsedConfig.rules);
          if (!priorityValidation.isValid) {
            setPriorityValidationError(priorityValidation.error);
            setImportError(null);
            setImportPreview(parsedConfig); // Still show preview but disable import
            // Reset file input to allow selecting the same file again
            fileInput.value = '';
            return;
          } else {
            setPriorityValidationError(null);
          }
        }

        // Validate templates structure
        if (parsedConfig.templates) {
          const templateValidation = validateTemplatesStructure(parsedConfig.templates);
          if (!templateValidation.isValid) {
            setImportError(`Invalid templates structure: ${templateValidation.errors.join(', ')}`);
            setPriorityValidationError(null);
            // Reset file input to allow selecting the same file again
            fileInput.value = '';
            return;
          }
        }

        setImportPreview(parsedConfig);
        setImportError(null);
        setPriorityValidationError(null);
        // Reset file input to allow selecting the same file again
        fileInput.value = '';
      } catch (error) {
        setImportError('Invalid JSON format: ' + (error as Error).message);
        setPriorityValidationError(null);
        // Reset file input to allow selecting the same file again
        fileInput.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleImportRules = async () => {
    if (!selectedFile || !importPreview) return;

    try {
      setLoading(true);
      
      // Transform the imported JSON to match backend format
      const transformedConfig = transformImportData(importPreview);
      
      // Import the configuration
      await tagopsService.updateRuleConfig(transformedConfig);
      
      // Reload the data to reflect changes
      await loadData();
      
      setImportSuccess('Configuration imported successfully!');
      setImportError(null);
      
      // Clear the form after successful import
      setTimeout(() => {
        setImportWizardOpen(false);
        setSelectedFile(null);
        setImportPreview(null);
        setImportSuccess(null);
        setImportError(null);
      }, 2000);
      
    } catch (error) {
      console.error('Error importing rules:', error);
      setImportError('Failed to import rules: ' + (error as Error).message);
      setImportSuccess(null);
    } finally {
      setLoading(false);
    }
  };

  // Transform imported data to match backend format
  const transformImportData = (importData: any) => {
    const transformedConfig: any = {
      startRule: importData.startRule || "0", // Use existing startRule or default
      templates: {},
      rules: {}
    };

    // Keep templates as object format (same as file format)
    if (importData.templates && typeof importData.templates === 'object') {
      if (Array.isArray(importData.templates)) {
        // Handle array format (legacy support)
        importData.templates.forEach((templateObj: any) => {
          Object.entries(templateObj).forEach(([templateName, templateData]: [string, any]) => {
            transformedConfig.templates[templateName] = templateData;
          });
        });
      } else {
        // Already in object format - use as is
        transformedConfig.templates = importData.templates;
      }
    }

    // Keep rules as object format (same as file format)
    if (importData.rules && typeof importData.rules === 'object') {
      // Check if it's already in numbered format (legacy support)
      const firstKey = Object.keys(importData.rules)[0];
      if (firstKey && /^\d+$/.test(firstKey)) {
        // Transform from numbered format to object format
        Object.entries(importData.rules).forEach(([groupKey, ruleGroup]: [string, any]) => {
          if (typeof ruleGroup === 'object' && ruleGroup !== null) {
            Object.entries(ruleGroup).forEach(([ruleName, ruleData]: [string, any]) => {
              // Remove the name from the rule data since it's the key
              const { name, ...ruleWithoutName } = ruleData as any;
              transformedConfig.rules[ruleName] = ruleWithoutName;
            });
          }
        });
      } else {
        // Already in object format - use as is, but remove name field if present
        Object.entries(importData.rules).forEach(([ruleName, ruleData]: [string, any]) => {
          // Only process valid rule objects (skip metadata and other non-rule properties)
          if (ruleData && typeof ruleData === 'object' && (ruleData.rule || ruleData.name || ruleData.condition)) {
            // Remove the name from the rule data since it's the key
            const { name, ...ruleWithoutName } = ruleData;
            transformedConfig.rules[ruleName] = ruleWithoutName;
          }
        });
      }
    }

    return transformedConfig;
  };


  const removeGeneralTag = (tagName: string) => {
    updateTemplateData({
      generalTags: templateData.generalTags.filter(tag => tag !== tagName)
    });
  };

  const addConstantTag = () => {
    updateTemplateData({
      constantTags: [...templateData.constantTags, { '': '' }]
    });
  };

  const removeConstantTag = (index: number) => {
    updateTemplateData({
      constantTags: templateData.constantTags.filter((_, i) => i !== index)
    });
  };

  const updateConstantTag = (index: number, key: string, value: string) => {
    const newConstantTags = [...templateData.constantTags];
    newConstantTags[index] = { [key]: value };
    updateTemplateData({ constantTags: newConstantTags });
  };

  // Function to validate template
  const validateTemplate = useCallback(() => {
    const errors: Record<string, string[]> = {};
    const stepErrors: Record<number, string[]> = {};

    // Validate basic information (Step 0)
    if (!templateData.name?.trim()) {
      errors.name = ['Template name is required'];
      stepErrors[0] = stepErrors[0] || [];
      stepErrors[0].push('Template name is required');
    }

    if (!templateData.category) {
      errors.category = ['Category is required'];
      stepErrors[0] = stepErrors[0] || [];
      stepErrors[0].push('Category is required');
    }

    // Validate that at least one type of tag is present (either general tags OR constant tags)
    const hasGeneralTags = templateData.generalTags.length > 0;
    const hasConstantTags = templateData.constantTags.length > 0;

    if (!hasGeneralTags && !hasConstantTags) {
      errors.generalTags = ['At least one dynamic tag or constant tag is required'];
      errors.constantTags = ['At least one dynamic tag or constant tag is required'];
      stepErrors[1] = stepErrors[1] || [];
      stepErrors[1].push('At least one dynamic tag or constant tag is required');
      stepErrors[2] = stepErrors[2] || [];
      stepErrors[2].push('At least one dynamic tag or constant tag is required');
    }

    // Validate constant tags - if present, must be valid
    if (templateData.constantTags.length > 0) {
      const hasInvalidTags = templateData.constantTags.some(tag => {
        const tagKey = Object.keys(tag)[0] || '';
        const tagValue = Object.values(tag)[0] || '';
        return !tagKey.trim() || !tagValue.trim();
      });

      if (hasInvalidTags) {
        errors.constantTags = ['All constant tags must have valid key and value'];
        stepErrors[2] = stepErrors[2] || [];
        stepErrors[2].push('All constant tags must have valid key and value');
      }
    }

    setTemplateValidationErrors(errors);
    setTemplateStepErrors(stepErrors);
    return Object.keys(errors).length === 0;
  }, [templateData]);

  // Validate template when data changes
  useEffect(() => {
    validateTemplate();
  }, [templateData, validateTemplate]);

  // Check if template constant tag exists in other rules (exact and similar)
  const checkTemplateDuplicateTag = (tagKey: string, tagValue: string, tagIndex: number) => {
    if (!tagKey?.trim() || Object.keys(rules).length === 0) {
      setTemplateDuplicateTagWarnings(prev => {
        const next = { ...prev };
        delete next[tagIndex];
        return next;
      });
      setTemplateSimilarTagWarnings(prev => {
        const next = { ...prev };
        delete next[tagIndex];
        return next;
      });
      return;
    }
    const duplicateRules = getRulesContainingTag(rules, templates, tagKey, tagValue, null);
    const similarRules = getRulesContainingSimilarTag(rules, templates, tagKey, tagValue, null);
    setTemplateDuplicateTagWarnings(prev => {
      const next = { ...prev };
      if (duplicateRules.length > 0) {
        next[tagIndex] = duplicateRules;
      } else {
        delete next[tagIndex];
      }
      return next;
    });
    setTemplateSimilarTagWarnings(prev => {
      const next = { ...prev };
      if (similarRules.length > 0) {
        next[tagIndex] = similarRules;
      } else {
        delete next[tagIndex];
      }
      return next;
    });
  };

  // Function to validate individual template tag
  const validateTemplateTag = (tagKey: string, tagValue: string, tagIndex: number): boolean => {
    const validation = validateAWSTag(tagKey, tagValue);
    if (!validation.isValid) {
      setTemplateTagValidationErrors(prev => ({
        ...prev,
        [`template_tag_${tagIndex}`]: validation.errors
      }));
      return false;
    } else {
      setTemplateTagValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`template_tag_${tagIndex}`];
        return newErrors;
      });
      return true;
    }
  };


  // Dynamic tag functionality
  const addDynamicTag = (tagType: string) => {
    if (!templateData.generalTags.includes(tagType)) {
      updateTemplateData({
        generalTags: [...templateData.generalTags, tagType]
      });
    }
  };

  const removeDynamicTag = (tagType: string) => {
    updateTemplateData({
      generalTags: templateData.generalTags.filter(tag => tag !== tagType)
    });
  };

  const isDynamicTagSelected = (tagType: string) => {
    return templateData.generalTags.includes(tagType);
  };

  const renderTemplateStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Define the basic properties of your template.
            </Typography>

            {/* Validation Summary for this step */}
            {(templateValidationErrors.name || templateValidationErrors.category) && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <AlertTitle>Required Fields Missing</AlertTitle>
                <Box sx={{ ml: 2 }}>
                  {templateValidationErrors.name && (
                    <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <strong>Template Name:</strong> {templateValidationErrors.name.join(', ')}
                    </Typography>
                  )}
                  {templateValidationErrors.category && (
                    <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <strong>Category:</strong> {templateValidationErrors.category.join(', ')}
                    </Typography>
                  )}
                </Box>
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={templateData.name}
                  onChange={(e) => updateTemplateData({ name: e.target.value })}
                  placeholder="e.g., Production Cost Center Template"
                  required
                  inputProps={{ maxLength: 64 }}
                  helperText={templateValidationErrors.name ? templateValidationErrors.name.join(', ') : `${templateData.name.length}/64 characters`}
                  error={!!templateValidationErrors.name}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={templateData.description}
                  onChange={(e) => updateTemplateData({ description: e.target.value })}
                  placeholder="Describe what this template is used for"
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 512 }}
                  helperText={`${templateData.description.length}/512 characters`}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Category
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Select a category for your template to help organize and filter templates.
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(RULE_CATEGORIES).map(([key, category]) => (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <Card
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor: templateData.category === key ? category.color : 'divider',
                          bgcolor: templateData.category === key ? `${category.color}05` : 'background.paper',
                          '&:hover': {
                            borderColor: category.color,
                            bgcolor: `${category.color}05`,
                          },
                        }}
                        onClick={() => updateTemplateData({ category: key })}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box color={category.color}>
                            {category.icon}
                          </Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {category.name}
                          </Typography>
                          {templateData.category === key && (
                            <CheckCircleIcon color="primary" sx={{ ml: 'auto' }} />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {category.description}
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Dynamic Tags
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add tags that users will be prompted to provide values for when applying this template. This step is optional if you plan to add constant tags instead.
            </Typography>

            {/* Validation Summary for this step */}
            {templateValidationErrors.generalTags && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <AlertTitle>Tags Required</AlertTitle>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <strong>Dynamic Tags:</strong> {templateValidationErrors.generalTags.join(', ')}
                  </Typography>
                </Box>
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Selected Dynamic Tags
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                  {templateData.generalTags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => removeGeneralTag(tag)}
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
                
                <Typography variant="subtitle1" gutterBottom>
                  Available Dynamic Tag Types
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Select from the available dynamic tag types:
                </Typography>
                
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {DYNAMIC_TAG_TYPES.map((tagType) => (
                    <Chip
                      key={tagType}
                      label={tagType}
                      size="medium"
                      variant={isDynamicTagSelected(tagType) ? "filled" : "outlined"}
                      color={isDynamicTagSelected(tagType) ? "primary" : "default"}
                      onClick={() => isDynamicTagSelected(tagType) ? removeDynamicTag(tagType) : addDynamicTag(tagType)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isDynamicTagSelected(tagType) ? 'primary.dark' : 'action.hover'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Constant Tags
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add tags with fixed values that will be applied automatically. This step is optional if you plan to add dynamic tags instead.
            </Typography>

            {/* Validation Summary for this step */}
            {templateValidationErrors.constantTags && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <AlertTitle>Constant Tag Issues</AlertTitle>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <strong>Constant Tags:</strong> {templateValidationErrors.constantTags.join(', ')}
                  </Typography>
                </Box>
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                {templateData.constantTags.map((tag, index) => {
                  const tagKey = Object.keys(tag)[0] || '';
                  const tagValue = Object.values(tag)[0] || '';
                  const tagErrors = templateTagValidationErrors[`template_tag_${index}`] || [];
                  const hasErrors = tagErrors.length > 0;
                  
                  return (
                    <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: hasErrors ? 'error.main' : 'divider' }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <TextField
                          label="Tag Key"
                          value={tagKey}
                          onChange={(e) => {
                            updateConstantTag(index, e.target.value, tagValue);
                            validateTemplateTag(e.target.value, tagValue, index);
                            checkTemplateDuplicateTag(e.target.value, tagValue, index);
                          }}
                          onBlur={() => {
                            validateTemplateTag(tagKey, tagValue, index);
                            checkTemplateDuplicateTag(tagKey, tagValue, index);
                          }}
                          sx={{ flex: 1 }}
                          placeholder="e.g., Environment"
                          error={hasErrors}
                          helperText={hasErrors ? tagErrors.join(', ') : ''}
                        />
                        <TextField
                          label="Tag Value"
                          value={tagValue}
                          onChange={(e) => {
                            updateConstantTag(index, tagKey, e.target.value);
                            validateTemplateTag(tagKey, e.target.value, index);
                            checkTemplateDuplicateTag(tagKey, e.target.value, index);
                          }}
                          onBlur={() => {
                            validateTemplateTag(tagKey, tagValue, index);
                            checkTemplateDuplicateTag(tagKey, tagValue, index);
                          }}
                          sx={{ flex: 1 }}
                          placeholder="e.g., Production"
                          error={hasErrors}
                          helperText={hasErrors ? tagErrors.join(', ') : ''}
                        />
                        <PermissionIconButton
                          permission="PowerUser"
                          onClick={() => {
                            removeConstantTag(index);
                            setTemplateTagValidationErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors[`template_tag_${index}`];
                              return newErrors;
                            });
                            setTemplateDuplicateTagWarnings(prev => {
                              const newWarnings = { ...prev };
                              delete newWarnings[index];
                              return newWarnings;
                            });
                            setTemplateSimilarTagWarnings(prev => {
                              const newWarnings = { ...prev };
                              delete newWarnings[index];
                              return newWarnings;
                            });
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </PermissionIconButton>
                      </Box>
                    </Paper>
                  );
                })}
                
                <PermissionButton
                  permission="PowerUser"
                  startIcon={<AddIcon />}
                  onClick={addConstantTag}
                  variant="outlined"
                >
                  Add Constant Tag
                </PermissionButton>

                {/* Display duplicate tag warnings */}
                {Object.keys(templateDuplicateTagWarnings).length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Duplicate tag detected
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      The same tag key-value combination already exists in the following rule(s):
                    </Typography>
                    <Stack spacing={1}>
                      {Object.entries(templateDuplicateTagWarnings).map(([tagIndex, ruleNames]) => {
                        const idx = parseInt(tagIndex);
                        const tag = templateData.constantTags[idx];
                        const tagKey = tag ? Object.keys(tag)[0] : '';
                        const tagValue = tag ? Object.values(tag)[0] : '';
                        return (
                          <Box key={tagIndex}>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                              {tagKey}={tagValue || '(empty)'}:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {ruleNames.map(ruleName => (
                                <PermissionButton
                                  key={ruleName}
                                  permission="PowerUser"
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => {
                                    setTemplateWizardOpen(false);
                                    setEditingTemplate(null);
                                    setTemplateData({ name: '', description: '', category: '', generalTags: [], constantTags: [] });
                                    setTemplateWizardStep(0);
                                    setTemplateDuplicateTagWarnings({});
                                    setTemplateSimilarTagWarnings({});
                                    setTimeout(() => {
                                      setEditingRule(ruleName);
                                      setRuleWizardOpen(true);
                                    }, 0);
                                  }}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Edit &quot;{ruleName}&quot;
                                </PermissionButton>
                              ))}
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Alert>
                )}

                {/* Display similar tag warnings */}
                {Object.keys(templateSimilarTagWarnings).length > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Similar tag key detected
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      A similar tag key (different casing) already exists in another rule. AWS tag keys are case-sensitive, so these may be unintentional duplicates.
                    </Typography>
                    <Stack spacing={1}>
                      {Object.entries(templateSimilarTagWarnings).map(([tagIndex, similarList]) => {
                        const idx = parseInt(tagIndex);
                        const tag = templateData.constantTags[idx];
                        const tagKey = tag ? Object.keys(tag)[0] : '';
                        const uniqueByRule = Array.from(
                          new Map(similarList.map(s => [s.ruleName, s])).values()
                        );
                        return (
                          <Box key={tagIndex}>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                              Your &quot;{tagKey}&quot; is similar to:
                            </Typography>
                            {uniqueByRule.map(({ ruleName, existingKey }) => (
                              <Box key={`${ruleName}-${existingKey}`} display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                                <Typography variant="body2">
                                  &quot;{existingKey}&quot; in rule &quot;{ruleName}&quot;
                                </Typography>
                                <PermissionButton
                                  permission="PowerUser"
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => {
                                    setTemplateWizardOpen(false);
                                    setEditingTemplate(null);
                                    setTemplateData({ name: '', description: '', category: '', generalTags: [], constantTags: [] });
                                    setTemplateWizardStep(0);
                                    setTemplateDuplicateTagWarnings({});
                                    setTemplateSimilarTagWarnings({});
                                    setTimeout(() => {
                                      setEditingRule(ruleName);
                                      setRuleWizardOpen(true);
                                    }, 0);
                                  }}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Edit rule
                                </PermissionButton>
                              </Box>
                            ))}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Alert>
                )}

                {/* Display template blacklisted tag error */}
                {templateBlacklistedTagError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {templateBlacklistedTagError}
                  </Alert>
                )}

                {/* Display template tag validation errors */}
                {Object.keys(templateTagValidationErrors).length > 0 && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Tag Validation Errors:
                    </Typography>
                    {Object.entries(templateTagValidationErrors).map(([tagIndex, errors]) => (
                      <Box key={tagIndex} sx={{ mb: 1 }}>
                        <Typography variant="body2" color="error">
                          Tag {parseInt(tagIndex.split('_')[2]) + 1}: {errors.join(', ')}
                        </Typography>
                      </Box>
                    ))}
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Save
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Review your template configuration before saving.
            </Typography>

            {/* Validation Summary Card */}
            <Grid container spacing={3} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Card 
                  sx={{ 
                    border: '1px solid',
                    borderColor: !isTemplateValid ? 'error.main' : 'success.main',
                    bgcolor: 'background.paper'
                  }}
                >
                  <CardHeader
                    title={
                      <Box display="flex" alignItems="center" gap={1}>
                        {!isTemplateValid ? <ErrorIcon sx={{ color: 'text.secondary' }} /> : <CheckCircleIcon color="success" />}
                        <Typography variant="h6">
                          {!isTemplateValid ? 'Validation Issues Found' : 'All Validations Passed'}
                        </Typography>
                      </Box>
                    }
                    action={
                      <Chip
                        label={!isTemplateValid ? 'Needs Fixes' : 'Ready to Save'}
                        color={!isTemplateValid ? 'error' : 'success'}
                        size="small"
                      />
                    }
                  />
                  <CardContent>
                    {!isTemplateValid ? (
                      <Box>
                        <Typography variant="body2" color="error" gutterBottom>
                          The following steps have validation issues that need to be resolved:
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                          {(templateValidationErrors.name || templateValidationErrors.category) && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => setTemplateWizardStep(0)}
                              startIcon={<ErrorIcon />}
                            >
                              Fix Basic Info
                            </Button>
                          )}
                          {templateValidationErrors.generalTags && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => setTemplateWizardStep(1)}
                              startIcon={<ErrorIcon />}
                            >
                              Fix General Tags
                            </Button>
                          )}
                          {templateValidationErrors.constantTags && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => setTemplateWizardStep(2)}
                              startIcon={<ErrorIcon />}
                            >
                              Fix Constant Tags
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="success.main">
                        ✓ All required fields are filled correctly. You can now save your template.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Duplicate / Similar tag warnings (proceeded despite warnings) */}
              {(Object.keys(templateDuplicateTagWarnings).length > 0 || Object.keys(templateSimilarTagWarnings).length > 0) && (
                <Grid item xs={12}>
                  {Object.keys(templateDuplicateTagWarnings).length > 0 && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Duplicate tag detected
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        The same tag key-value combination already exists in the following rule(s):
                      </Typography>
                      <Stack spacing={1}>
                        {Object.entries(templateDuplicateTagWarnings).map(([tagIndex, ruleNames]) => {
                          const idx = parseInt(tagIndex);
                          const tag = templateData.constantTags[idx];
                          const tagKey = tag ? Object.keys(tag)[0] : '';
                          const tagValue = tag ? Object.values(tag)[0] : '';
                          return (
                            <Box key={tagIndex}>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                {tagKey}={tagValue || '(empty)'}:
                              </Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {ruleNames.map(ruleName => (
                                  <PermissionButton
                                    key={ruleName}
                                    permission="PowerUser"
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => {
                                      setTemplateWizardOpen(false);
                                      setEditingTemplate(null);
                                      setTemplateData({ name: '', description: '', category: '', generalTags: [], constantTags: [] });
                                      setTemplateWizardStep(0);
                                      setTemplateDuplicateTagWarnings({});
                                      setTemplateSimilarTagWarnings({});
                                      setTimeout(() => {
                                        setEditingRule(ruleName);
                                        setRuleWizardOpen(true);
                                      }, 0);
                                    }}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Edit &quot;{ruleName}&quot;
                                  </PermissionButton>
                                ))}
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Alert>
                  )}
                  {Object.keys(templateSimilarTagWarnings).length > 0 && (
                    <Alert severity="info">
                      <Typography variant="subtitle2" gutterBottom>
                        Similar tag key detected
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        A similar tag key (different casing) already exists in another rule. AWS tag keys are case-sensitive, so these may be unintentional duplicates.
                      </Typography>
                      <Stack spacing={1}>
                        {Object.entries(templateSimilarTagWarnings).map(([tagIndex, similarList]) => {
                          const idx = parseInt(tagIndex);
                          const tag = templateData.constantTags[idx];
                          const tagKey = tag ? Object.keys(tag)[0] : '';
                          const uniqueByRule = Array.from(
                            new Map(similarList.map(s => [s.ruleName, s])).values()
                          );
                          return (
                            <Box key={tagIndex}>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                Your &quot;{tagKey}&quot; is similar to:
                              </Typography>
                              {uniqueByRule.map(({ ruleName, existingKey }) => (
                                <Box key={`${ruleName}-${existingKey}`} display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                                  <Typography variant="body2">
                                    &quot;{existingKey}&quot; in rule &quot;{ruleName}&quot;
                                  </Typography>
                                  <PermissionButton
                                    permission="PowerUser"
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => {
                                      setTemplateWizardOpen(false);
                                      setEditingTemplate(null);
                                      setTemplateData({ name: '', description: '', category: '', generalTags: [], constantTags: [] });
                                      setTemplateWizardStep(0);
                                      setTemplateDuplicateTagWarnings({});
                                      setTemplateSimilarTagWarnings({});
                                      setTimeout(() => {
                                        setEditingRule(ruleName);
                                        setRuleWizardOpen(true);
                                      }, 0);
                                    }}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Edit rule
                                  </PermissionButton>
                                </Box>
                              ))}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Alert>
                  )}
                </Grid>
              )}
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Template Summary" />
                  <CardContent>
                    <Grid container spacing={2}>
                                             <Grid item xs={12} md={6}>
                         <Typography variant="subtitle2" color="text.secondary">
                           Name
                         </Typography>
                         <Typography variant="body1" gutterBottom>
                           {templateData.name || 'No name provided'}
                         </Typography>
                       </Grid>
                       
                       <Grid item xs={12} md={6}>
                         <Typography variant="subtitle2" color="text.secondary">
                           Category
                         </Typography>
                         <Typography variant="body1" gutterBottom>
                           {templateData.category ? RULE_CATEGORIES[templateData.category as keyof typeof RULE_CATEGORIES]?.name || templateData.category : 'No category selected'}
                         </Typography>
                       </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Description
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {templateData.description || 'No description provided'}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          General Tags ({templateData.generalTags.length})
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {templateData.generalTags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={`${tag}=*`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {templateData.generalTags.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              No general tags defined
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Constant Tags ({templateData.constantTags.length})
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {templateData.constantTags.map((tag, index) => {
                            const tagKey = Object.keys(tag)[0] || '';
                            const tagValue = Object.values(tag)[0] || '';
                            return (
                              <Chip
                                key={index}
                                label={`${tagKey}=${tagValue}`}
                                size="small"
                                variant="outlined"
                              />
                            );
                          })}
                          {templateData.constantTags.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              No constant tags defined
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {!isTemplateValid && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Validation Issues Found</AlertTitle>
                <Typography variant="body2" gutterBottom>
                  Please fix the following issues before saving:
                </Typography>
                
                {/* Basic Information Issues */}
                {(templateValidationErrors.name || templateValidationErrors.category) && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Step 1: Basic Information
                    </Typography>
                    <Box sx={{ ml: 2 }}>
                      {templateValidationErrors.name && (
                        <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          {templateValidationErrors.name.join(', ')}
                        </Typography>
                      )}
                      {templateValidationErrors.category && (
                        <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          {templateValidationErrors.category.join(', ')}
                        </Typography>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => setTemplateWizardStep(0)}
                        sx={{ mt: 1 }}
                      >
                        Go to Basic Information
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* General Tags Issues */}
                {templateValidationErrors.generalTags && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Step 2: General Tags
                    </Typography>
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        {templateValidationErrors.generalTags.join(', ')}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => setTemplateWizardStep(1)}
                        sx={{ mt: 1 }}
                      >
                        Go to General Tags
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Constant Tags Issues */}
                {templateValidationErrors.constantTags && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Step 3: Constant Tags
                    </Typography>
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ErrorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        {templateValidationErrors.constantTags.join(', ')}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => setTemplateWizardStep(2)}
                        sx={{ mt: 1 }}
                      >
                        Go to Constant Tags
                      </Button>
                    </Box>
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const filteredRules = useMemo(() => {
    let filtered = Object.entries(rules);

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(([name, rule]) => {
        const searchLower = searchQuery.toLowerCase();
        return (
          name.toLowerCase().includes(searchLower) ||
          (rule.description && rule.description.toLowerCase().includes(searchLower)) ||
          (rule.category && rule.category.toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(([, rule]) => rule.category === filterCategory);
    }

    // Apply sorting - ALWAYS sort by priority first (ascending: 1, 2, 3...)
    filtered.sort(([aName, a], [bName, b]) => {
      // First, always sort by priority (ascending - lower numbers first)
      const aPriority = a.priority ?? 9999;
      const bPriority = b.priority ?? 9999;
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      // If priorities are equal, apply the selected sort
      let aValue, bValue;
      switch (sortBy) {
        case 'priority':
          // Already sorted by priority above
          return 0;
        case 'name':
          aValue = aName || '';
          bValue = bName || '';
          break;
        case 'category':
          aValue = a.category || '';
          bValue = b.category || '';
          break;
        case 'created':
          aValue = a.createdAt || '';
          bValue = b.createdAt || '';
          break;
        case 'updated':
          aValue = a.updatedAt || '';
          bValue = b.updatedAt || '';
          break;
        default:
          aValue = aName || '';
          bValue = bName || '';
      }

      if (sortOrder === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    return filtered;
  }, [rules, searchQuery, filterCategory, sortBy, sortOrder]);

  const filteredTemplates = useMemo(() => {
    let filtered = Object.entries(templates);

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(([name, template]) => {
        const searchLower = searchQuery.toLowerCase();
        return (
          name.toLowerCase().includes(searchLower) ||
          (template.description && template.description.toLowerCase().includes(searchLower)) ||
          (template.category && template.category.toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(([, template]) => template.category === filterCategory);
    }

    // Apply sorting
    filtered.sort(([aName, a], [bName, b]) => {
      let aValue, bValue;
      switch (sortBy) {
        case 'name':
          aValue = aName || '';
          bValue = bName || '';
          break;
        case 'category':
          aValue = a.category || '';
          bValue = b.category || '';
          break;
        case 'created':
          // Templates don't have createdAt, fall back to name
          aValue = aName || '';
          bValue = bName || '';
          break;
        case 'updated':
          // Templates don't have updatedAt, fall back to name
          aValue = aName || '';
          bValue = bName || '';
          break;
        default:
          aValue = aName || '';
          bValue = bName || '';
      }

      if (sortOrder === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    return filtered;
  }, [templates, searchQuery, filterCategory, sortBy, sortOrder]);

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Tagging Rules</Typography>
          <Skeleton variant="rectangular" width={200} height={40} />
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="40%" height={24} />
                <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Skeleton variant="text" width="50%" height={32} />
                <Skeleton variant="rectangular" width="100%" height={150} sx={{ mt: 2 }} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

      return (
      <Box>
        {/* Professional Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="primary">
            Tagging Rules Management
          </Typography>
        </Box>
        
                 <Box display="flex" gap={2}>
           <Button
             variant="outlined"
             startIcon={<RefreshIcon />}
             onClick={loadData}
             disabled={loading}
           >
             Refresh
           </Button>
           <PermissionButton
             permission="PowerUser"
             variant="contained"
             startIcon={<AddIcon />}
             onClick={() => setRuleWizardOpen(true)}
             sx={{
               backgroundColor: 'rgba(255, 255, 255, 0.9)',
               color: '#0f172a',
               '&:hover': {
                 backgroundColor: 'rgba(255, 255, 255, 1)',
                 color: '#0f172a',
               },
               '&.Mui-disabled': {
                 backgroundColor: 'rgba(255, 255, 255, 0.5)',
                 color: 'rgba(0, 0, 0, 0.26)',
               },
             }}
           >
             Create Rule
           </PermissionButton>
           <PermissionButton
             permission="PowerUser"
             variant="contained"
             startIcon={<TemplateIcon />}
             onClick={() => {
               // Reset form data for new template
               setTemplateData({
                 name: '',
                 description: '',
                 category: '',
                 generalTags: [],
                 constantTags: []
               });
               setTemplateWizardStep(0);
               setEditingTemplate(null);
               setTemplateDuplicateTagWarnings({});
               setTemplateSimilarTagWarnings({});
               setTemplateWizardOpen(true);
             }}
             sx={{
               backgroundColor: 'rgba(255, 255, 255, 0.9)',
               color: '#0f172a',
               '&:hover': {
                 backgroundColor: 'rgba(255, 255, 255, 1)',
                 color: '#0f172a',
               },
               '&.Mui-disabled': {
                 backgroundColor: 'rgba(255, 255, 255, 0.5)',
                 color: 'rgba(0, 0, 0, 0.26)',
               },
             }}
           >
             Create Template
           </PermissionButton>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportWizardOpen(true)}
            sx={{
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'action.hover',
                color: 'primary.dark',
              },
            }}
          >
            Import Configuration
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'action.hover',
                color: 'primary.dark',
              },
            }}
          >
            Export Configuration
          </Button>
         </Box>
      </Box>

      {/* View Mode Toggle */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            View Mode:
          </Typography>
          <Button
            variant={viewMode === 'compact' ? 'contained' : 'outlined'}
            size="small"
            startIcon={<ViewListIcon />}
            onClick={() => setViewMode('compact')}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            Compact
          </Button>
          <Button
            variant={viewMode === 'detailed' ? 'contained' : 'outlined'}
            size="small"
            startIcon={<ViewModuleIcon />}
            onClick={() => setViewMode('detailed')}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            Detailed
          </Button>
        </Box>
      </Box>

      {/* Enhanced Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search rules, templates, or categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterCategory}
                  label="Category"
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {Object.entries(RULE_CATEGORIES).map(([key, category]) => (
                    <MenuItem key={key} value={key}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {category.icon}
                        {category.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value as any)}
                >
                  <MenuItem value="priority">Priority</MenuItem>
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="category">Category</MenuItem>
                  <MenuItem value="created">Created</MenuItem>
                  <MenuItem value="updated">Updated</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            

            
          </Grid>

        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedRules.length > 0 && (
        (() => {
          const allSelectedEnabled = selectedRules.every(ruleName => rules[ruleName]?.enabled !== false);
          const allSelectedDisabled = selectedRules.every(ruleName => rules[ruleName]?.enabled === false);

          const enableDisabled = saving || allSelectedEnabled;
          const disableDisabled = saving || allSelectedDisabled;

          return (
            <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">
                    {selectedRules.length} rule(s) selected
                  </Typography>
                  <Box display="flex" gap={1}>
                    <PermissionButton
                      permission="PowerUser"
                      size="small"
                      variant="contained"
                      onClick={() => handleBulkAction('enable')}
                      disabled={enableDisabled}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: '#0f172a',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          color: '#0f172a',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(255, 255, 255, 0.5)',
                          color: 'rgba(0, 0, 0, 0.26)',
                        },
                      }}
                    >
                      Enable
                    </PermissionButton>
                    <PermissionButton
                      permission="PowerUser"
                      size="small"
                      variant="contained"
                      onClick={() => handleBulkAction('disable')}
                      disabled={disableDisabled}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: '#0f172a',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          color: '#0f172a',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(255, 255, 255, 0.5)',
                          color: 'rgba(0, 0, 0, 0.26)',
                        },
                      }}
                    >
                      Disable
                    </PermissionButton>
                    <PermissionButton
                      permission="PowerUser"
                      size="small"
                      variant="contained"
                      onClick={() => handleBulkAction('duplicate')}
                      disabled={saving}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: '#0f172a',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          color: '#0f172a',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(255, 255, 255, 0.5)',
                          color: 'rgba(0, 0, 0, 0.26)',
                        },
                      }}
                    >
                      Duplicate
                    </PermissionButton>
                    <PermissionButton
                      permission="PowerUser"
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={() => handleBulkAction('delete')}
                      disabled={saving}
                    >
                      Delete
                    </PermissionButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })()
      )}

             {/* Main Content */}
       <Grid container spacing={3}>
         {/* Left Column - Rules and Templates */}
         <Grid item xs={12} md={8}>
           <Stack spacing={3}>
             {/* Rules Section */}
             <Card>
               <CardHeader
                 title={
                   <Box display="flex" alignItems="center" gap={1}>
                     <IconButton
                       onClick={() => setRulesSectionExpanded(!rulesSectionExpanded)}
                       size="small"
                       sx={{ mr: 1 }}
                     >
                       {rulesSectionExpanded ? <ExpandMoreIcon /> : <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />}
                     </IconButton>
                     <RuleIcon color="primary" />
                     <Typography variant="h6">
                       Rules ({filteredRules.length})
                     </Typography>
                   </Box>
                 }
               />
               <Collapse in={rulesSectionExpanded}>
                 <CardContent>
                   {filteredRules.length === 0 ? (
                     <Box textAlign="center" py={4}>
                       <RuleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                       <Typography variant="h6" color="text.secondary" gutterBottom>
                         No rules found
                       </Typography>
                       <Typography variant="body2" color="text.secondary" mb={2}>
                         {searchQuery || filterCategory !== 'all' 
                           ? 'Try adjusting your search or filters'
                           : 'Create your first tagging rule to get started'
                         }
                       </Typography>
                       <PermissionButton
                         permission="PowerUser"
                         variant="contained"
                         startIcon={<AddIcon />}
                         onClick={() => setRuleWizardOpen(true)}
                       >
                         Create First Rule
                       </PermissionButton>
                     </Box>
                  ) : (
                    <Box>
                      <DndContextWrapper
                        filteredRules={filteredRules}
                        rules={rules}
                        setRules={setRules}
                        setSaving={setSaving}
                        handleSaveRules={handleSaveRules}
                        showNotification={showNotification}
                        createSuccessNotification={createSuccessNotification}
                        createErrorNotification={createErrorNotification}
                        viewMode={viewMode}
                        selectedRules={selectedRules}
                        setSelectedRules={setSelectedRules}
                        handleEditRule={handleEditRule}
                        handleDuplicateRule={handleDuplicateRule}
                        handleDeleteRule={handleDeleteRule}
                        handleToggleRuleStatus={handleToggleRuleStatus}
                        renderCompactRuleCard={renderCompactRuleCard}
                        renderDetailedRuleCard={renderDetailedRuleCard}
                        loadData={loadData}
                      />
                    </Box>
                  )}
                 </CardContent>
               </Collapse>
             </Card>

             {/* Templates Section */}
             <Card>
               <CardHeader
                 title={
                   <Box display="flex" alignItems="center" gap={1}>
                     <IconButton
                       onClick={() => setTemplatesSectionExpanded(!templatesSectionExpanded)}
                       size="small"
                       sx={{ mr: 1 }}
                     >
                       {templatesSectionExpanded ? <ExpandMoreIcon /> : <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />}
                     </IconButton>
                     <TemplateIcon color="primary" />
                     <Typography variant="h6">
                       Templates ({filteredTemplates.length})
                     </Typography>
                   </Box>
                 }
               />
               <Collapse in={templatesSectionExpanded}>
                 <CardContent>
                   {filteredTemplates.length === 0 ? (
                     <Box textAlign="center" py={4}>
                       <TemplateIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                       <Typography variant="h6" color="text.secondary" gutterBottom>
                         No templates found
                       </Typography>
                       <Typography variant="body2" color="text.secondary" mb={2}>
                         {searchQuery || filterCategory !== 'all' 
                           ? 'Try adjusting your search or filters'
                           : 'Create your first template to get started'
                         }
                       </Typography>
                       <PermissionButton
                         permission="PowerUser"
                         variant="contained"
                         startIcon={<AddIcon />}
                         onClick={() => {
                           // Reset form data for new template
                           setTemplateData({
                             name: '',
                             description: '',
                             category: '',
                             generalTags: [],
                             constantTags: []
                           });
                           setTemplateWizardStep(0);
                           setEditingTemplate(null);
                           setTemplateDuplicateTagWarnings({});
                           setTemplateSimilarTagWarnings({});
                           setTemplateWizardOpen(true);
                         }}
                       >
                         Create First Template
                       </PermissionButton>
                     </Box>
                   ) : (
                     <Box>
                       {filteredTemplates.map(([name, template]) => (
                         <motion.div
                           key={name}
                           initial={{ opacity: 0, y: 20 }}
                           animate={{ opacity: 1, y: 0 }}
                           transition={{ duration: 0.3 }}
                         >
                           {viewMode === 'compact' 
                             ? renderCompactTemplateCard(
                                 name, 
                                 template, 
                                 handleEditTemplate, 
                                 handleDuplicateTemplate, 
                                 handleDeleteTemplate
                               )
                             : renderDetailedTemplateCard(
                                 name, 
                                 template, 
                                 handleEditTemplate, 
                                 handleDuplicateTemplate, 
                                 handleDeleteTemplate
                               )
                           }
                         </motion.div>
                       ))}
                     </Box>
                   )}
                 </CardContent>
               </Collapse>
             </Card>
           </Stack>
         </Grid>

         {/* Right Column - Sidebar */}
         <Grid item xs={12} md={4}>
           <Stack spacing={3}>


             {/* Professional Templates */}
             <PermissionWrapper permission="PowerUser">
               <Card>
                 <CardHeader 
                   title="Built-in Templates"
                   action={
                     <TextField
                       size="small"
                       placeholder="Search..."
                       value={templateSearchQuery}
                       onChange={(e) => setTemplateSearchQuery(e.target.value)}
                       InputProps={{
                         startAdornment: (
                           <InputAdornment position="start">
                             <SearchIcon />
                           </InputAdornment>
                         ),
                       }}
                       sx={{ minWidth: 150 }}
                     />
                   }
                 />
                 <CardContent>
                   <Box 
                     sx={{ 
                       maxHeight: 400, 
                       overflowY: 'auto',
                       border: '1px solid',
                       borderColor: 'divider',
                       borderRadius: 1,
                       p: 1,
                       bgcolor: 'background.paper'
                     }}
                   >
                     <Stack spacing={1}>
                       {Object.entries(PROFESSIONAL_TEMPLATES)
                         .filter(([key, template]) => {
                           if (!templateSearchQuery) return true;
                           const query = templateSearchQuery.toLowerCase();
                           return (
                             template.name.toLowerCase().includes(query) ||
                             template.description.toLowerCase().includes(query) ||
                             template.category.toLowerCase().includes(query) ||
                             template.tags.some(tag => 
                               tag.key.toLowerCase().includes(query) || 
                               tag.value.toLowerCase().includes(query)
                             )
                           );
                         })
                         .map(([key, template]) => (
                     <Paper
                       key={key}
                       sx={{
                         p: 1.5,
                         border: '1px solid',
                         borderColor: 'divider',
                         cursor: isReadOnly ? 'not-allowed' : 'pointer',
                         transition: 'all 0.2s ease-in-out',
                         opacity: isReadOnly ? 0.6 : 1,
                         '&:hover': {
                           borderColor: isReadOnly ? 'divider' : 'primary.main',
                           bgcolor: isReadOnly ? 'transparent' : 'primary.50',
                           transform: isReadOnly ? 'none' : 'translateY(-1px)',
                           boxShadow: isReadOnly ? 0 : 1,
                         },
                       }}
                       onClick={() => {
                          if (!isReadOnly) {
                            // Open rule wizard with template data
                            setSelectedTemplate(template);
                            setRuleWizardOpen(true);
                          }
                        }}
                     >
                       <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                         <Box color={RULE_CATEGORIES[template.category as keyof typeof RULE_CATEGORIES]?.color || 'primary.main'}>
                           {RULE_CATEGORIES[template.category as keyof typeof RULE_CATEGORIES]?.icon}
                         </Box>
                         <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                           {template.name}
                         </Typography>
                       </Box>
                       <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2, mb: 0.5, display: 'block' }}>
                         {template.description}
                       </Typography>
                       <Box display="flex" gap={0.25} flexWrap="wrap">
                         {template.tags.slice(0, 2).map((tag, index) => (
                           <Chip
                             key={index}
                             label={`${tag.key}${tag.value ? '=' + tag.value : ''}`}
                             size="small"
                             variant="outlined"
                             sx={{ 
                               fontSize: '0.65rem', 
                               height: 18,
                               '& .MuiChip-label': { px: 0.5 }
                             }}
                           />
                         ))}
                         {template.tags.length > 2 && (
                           <Chip
                             label={`+${template.tags.length - 2}`}
                             size="small"
                             variant="outlined"
                             sx={{ 
                               fontSize: '0.65rem', 
                               height: 18,
                               '& .MuiChip-label': { px: 0.5 }
                             }}
                           />
                         )}
                       </Box>
                     </Paper>
                   ))}
                     </Stack>
                   </Box>
                 </CardContent>
               </Card>
             </PermissionWrapper>

             {/* Category Distribution */}
             <Card>
               <CardHeader title="Categories" />
               <CardContent>
                 <Stack spacing={1}>
                   {Object.entries(ruleStats.byCategory).map(([category, count]) => (
                     <Box
                       key={category}
                       display="flex"
                       justifyContent="space-between"
                       alignItems="center"
                       p={1}
                       sx={{
                         bgcolor: 'grey.50',
                         borderRadius: 1,
                         cursor: 'pointer',
                         '&:hover': {
                           bgcolor: 'grey.100',
                         },
                       }}
                       onClick={() => setFilterCategory(category)}
                     >
                       <Box display="flex" alignItems="center" gap={1}>
                         {RULE_CATEGORIES[category as keyof typeof RULE_CATEGORIES]?.icon || <CategoryIcon />}
                         <Typography variant="body2">
                           {RULE_CATEGORIES[category as keyof typeof RULE_CATEGORIES]?.name || category}
                         </Typography>
                       </Box>
                       <Chip label={count} size="small" />
                     </Box>
                   ))}
                 </Stack>
               </CardContent>
             </Card>
           </Stack>
         </Grid>
      </Grid>



             {/* Loading Overlay */}
       {saving && (
         <Box
           position="fixed"
           top={0}
           left={0}
           right={0}
           zIndex={9999}
         >
           <LinearProgress />
         </Box>
       )}

       {/* Rule Wizard Dialog */}
       <RuleWizardProfessional
         open={ruleWizardOpen}
         onClose={() => {
           setRuleWizardOpen(false);
           setEditingRule(null);
           setSelectedTemplate(null);
         }}
         onSave={handleSaveRule}
         existingRule={editingRule && rules[editingRule] ? rules[editingRule] : undefined}
         selectedTemplate={selectedTemplate}
         templates={templates}
         serviceConfig={serviceConfig}
         onboardedRegions={onboardedRegions}
         onboardedAccounts={onboardedAccounts}
         rules={rules}
         editingRuleName={editingRule}
         onEditOtherRule={(ruleName) => {
           setRuleWizardOpen(false);
           setEditingRule(null);
           setSelectedTemplate(null);
           setTimeout(() => {
             setEditingRule(ruleName);
             setRuleWizardOpen(true);
           }, 0);
         }}
       />

               {/* Template Wizard Dialog */}
        <Dialog
            open={templateWizardOpen}
            onClose={() => {
              setTemplateWizardOpen(false);
              setEditingTemplate(null);
              setTemplateData({ name: '', description: '', category: '', generalTags: [], constantTags: [] });
              setTemplateWizardStep(0);
              setTemplateDuplicateTagWarnings({});
              setTemplateSimilarTagWarnings({});
            }}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: { 
                minHeight: '80vh'
              }
            }}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={2}>
                <TemplateIcon color="primary" />
                <Typography variant="h6">
                  {editingTemplate ? 'Edit Tag Template' : 'Create Tag Template'}
                </Typography>
              </Box>
              <IconButton onClick={() => {
                setTemplateWizardOpen(false);
                setEditingTemplate(null);
                setTemplateData({
                  name: '',
                  description: '',
                  category: '',
                  generalTags: [],
                  constantTags: []
                });
                setTemplateWizardStep(0);
                setTemplateDuplicateTagWarnings({});
                setTemplateSimilarTagWarnings({});
              }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            <StepIndicator 
              steps={templateSteps} 
              onStepClick={(stepId) => {
                const stepIndex = templateSteps.findIndex(s => s.id === stepId);
                if (stepIndex !== -1) {
                  setTemplateWizardStep(stepIndex);
                }
              }} 
            />

            <Box sx={{ p: 3 }}>
              <motion.div
                key={templateWizardStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderTemplateStepContent(templateWizardStep)}
              </motion.div>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Box display="flex" justifyContent="space-between" width="100%">
              <Button
                onClick={handleTemplateWizardBack}
                disabled={templateWizardStep === 0}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
              
              <Box display="flex" gap={2}>
                <Button onClick={() => {
                  setTemplateWizardOpen(false);
                  setEditingTemplate(null);
                  setTemplateData({
                    name: '',
                    description: '',
                    category: '',
                    generalTags: [],
                    constantTags: []
                  });
                  setTemplateDuplicateTagWarnings({});
                  setTemplateWizardStep(0);
                }} startIcon={<CancelIcon />}>
                  Cancel
                </Button>
                
                {templateWizardStep === templateSteps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleSaveTemplate}
                    disabled={!isTemplateValid || templateSaving}
                    startIcon={templateSaving ? <HourglassEmptyIcon /> : <SaveIcon />}
                  >
                    {templateSaving ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Create Template')}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleTemplateWizardNext}
                    endIcon={<ArrowForwardIcon />}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </DialogActions>

          {/* Loading Overlay */}
          {templateSaving && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              zIndex={9999}
            >
              <LinearProgress />
            </Box>
          )}
        </Dialog>



       {/* Import Wizard Dialog */}
       <Dialog
         open={importWizardOpen}
         onClose={() => {
           setImportWizardOpen(false);
           setSelectedFile(null);
           setImportPreview(null);
           setImportError(null);
           setImportSuccess(null);
           setPriorityValidationError(null);
         }}
         maxWidth="md"
         fullWidth
       >
         <DialogTitle>Import Configuration</DialogTitle>
         <DialogContent>
           <Box sx={{ mb: 3 }}>
             <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
               Upload a ruleconfig.json file to import rules and templates. The file should contain the complete rule configuration structure with rules and templates.
           </Typography>
             
             <Box sx={{ mb: 2 }}>
               <Button
                 variant="outlined"
                 startIcon={<DownloadIcon />}
                 onClick={handleDownloadTemplate}
                 sx={{ mr: 2 }}
               >
                 Download Example Template
               </Button>
               <Typography variant="caption" color="text.secondary">
                 Download an example template with sample rules and templates to understand the structure
               </Typography>
             </Box>
           </Box>

           <Box sx={{ mb: 3 }}>
             <input
               accept=".json"
               style={{ display: 'none' }}
               id="import-file-input"
               type="file"
               onChange={handleFileImport}
             />
             <label htmlFor="import-file-input">
               <Button
                 variant="contained"
                 component="span"
                 startIcon={<UploadIcon />}
                 fullWidth
                 sx={{ mb: 1 }}
               >
                 Choose File to Import
               </Button>
             </label>
             {selectedFile && (
               <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                 <Typography variant="body2" sx={{ fontWeight: 500 }}>
                   Selected File: {selectedFile.name}
                 </Typography>
                 <Typography variant="caption" color="text.secondary">
                   Size: {(selectedFile.size / 1024).toFixed(2)} KB
                 </Typography>
               </Box>
             )}
           </Box>

           {importError && (
             <Alert severity="error" sx={{ mb: 2 }}>
               <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                 Configuration Validation Failed
               </Typography>
               <Typography variant="body2">
                 {importError}
               </Typography>
             </Alert>
           )}

           {priorityValidationError && (
             <Alert severity="error" sx={{ mb: 2 }}>
               <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                 Priority Validation Failed
               </Typography>
               <Typography variant="body2">
                 {priorityValidationError}
               </Typography>
             </Alert>
           )}

           {importSuccess && (
             <Alert severity="success" sx={{ mb: 2 }}>
               {importSuccess}
             </Alert>
           )}

           {importPreview && (
             <Box sx={{ mt: 2 }}>
               <Typography variant="subtitle2" gutterBottom>
                 Import Preview:
               </Typography>
               
               {/* Validation Summary */}
               <Box sx={{ 
                 mb: 2, 
                 p: 2, 
                 bgcolor: priorityValidationError ? 'error.50' : 'success.50', 
                 borderRadius: 1, 
                 border: '1px solid', 
                 borderColor: priorityValidationError ? 'error.200' : 'success.200' 
               }}>
                 {priorityValidationError ? (
                   <>
                     <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.800', mb: 1 }}>
                       ⚠️ Configuration Invalid - Priority Validation Failed
                     </Typography>
                     <Typography variant="body2" color="error.700">
                       Found {(() => {
                         const rulesObj = importPreview.rules || {};
                         // Count only valid rule objects (exclude metadata and other non-rule properties)
                         return Object.entries(rulesObj).filter(([key, value]: [string, any]) => 
                           value && typeof value === 'object' && (value.rule || value.name || value.condition)
                         ).length;
                       })()} rule(s) and {Object.keys(importPreview.templates || {}).length} template(s)
                     </Typography>
                   </>
                 ) : (
                   <>
                     <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.800', mb: 1 }}>
                       ✅ Configuration Valid
                     </Typography>
                     <Typography variant="body2" color="success.700">
                       Found {(() => {
                         const rulesObj = importPreview.rules || {};
                         // Count only valid rule objects (exclude metadata and other non-rule properties)
                         return Object.entries(rulesObj).filter(([key, value]: [string, any]) => 
                           value && typeof value === 'object' && (value.rule || value.name || value.condition)
                         ).length;
                       })()} rule(s) and {Object.keys(importPreview.templates || {}).length} template(s)
                     </Typography>
                   </>
                 )}
               </Box>
               
               <Box sx={{ maxHeight: 300, overflow: 'auto', bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                 <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                   {JSON.stringify(transformImportData(importPreview), null, 2)}
                 </Typography>
               </Box>
               <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                 This shows the transformed data that will be imported to match the backend format.
               </Typography>
             </Box>
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={() => {
             setImportWizardOpen(false);
             setSelectedFile(null);
             setImportPreview(null);
             setImportError(null);
             setImportSuccess(null);
             setPriorityValidationError(null);
           }}>Cancel</Button>
           <Button
             onClick={handleImportRules}
             variant="contained"
             disabled={!selectedFile || !!importError || !!priorityValidationError}
             startIcon={<UploadIcon />}
           >
             Import Configuration
           </Button>
         </DialogActions>
       </Dialog>

       {/* Professional Delete Confirmation Dialog */}
       <Dialog
         open={deleteConfirmation.open}
         onClose={closeDeleteConfirmation}
         maxWidth="sm"
         fullWidth
         PaperProps={{
           sx: {
             borderRadius: 2,
             boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
           }
         }}
       >
         <DialogTitle sx={{ 
           display: 'flex', 
           alignItems: 'center', 
           gap: 2,
           pb: 1,
           color: 'error.main'
         }}>
           <Box
             sx={{
               width: 48,
               height: 48,
               borderRadius: '50%',
               bgcolor: '#dc2626',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               color: 'white',
               boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
             }}
           >
             <DeleteIcon sx={{ fontSize: 24 }} />
           </Box>
           <Box>
             <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
               Delete {deleteConfirmation.type === 'rule' ? 'Rule' : 'Template'}
             </Typography>
             <Typography variant="body2" color="text.secondary">
               This action cannot be undone
             </Typography>
           </Box>
         </DialogTitle>
         
         <DialogContent sx={{ pt: 2 }}>
           <Typography variant="body1" sx={{ mb: 2 }}>
             Are you sure you want to delete the {deleteConfirmation.type}{' '}
             <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
               "{deleteConfirmation.name}"
             </Box>?
           </Typography>
           
           <Alert severity="warning" sx={{ mt: 2 }}>
             <AlertTitle>Warning</AlertTitle>
             This will permanently remove the {deleteConfirmation.type} from your configuration. 
             {deleteConfirmation.type === 'rule' && ' Any resources currently using this rule will no longer be tagged automatically.'}
             {deleteConfirmation.type === 'template' && ' Any rules using this template will be affected.'}
           </Alert>
         </DialogContent>
         
         <DialogActions sx={{ p: 3, pt: 1 }}>
           <Button
             onClick={closeDeleteConfirmation}
             variant="outlined"
             startIcon={<CancelIcon />}
             sx={{ mr: 2 }}
           >
             Cancel
           </Button>
           <Button
             onClick={deleteConfirmation.onConfirm}
             variant="contained"
             color="error"
             startIcon={saving ? <HourglassEmptyIcon /> : <DeleteIcon />}
             disabled={saving}
             sx={{
               '&:hover': {
                 bgcolor: 'error.dark'
               }
             }}
           >
             {saving ? 'Deleting...' : `Delete ${deleteConfirmation.type === 'rule' ? 'Rule' : 'Template'}`}
           </Button>
         </DialogActions>
       </Dialog>
     </Box>
   );
 };
